import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import {
  Search,
  MessageSquarePlus,
  MoreVertical,
  Pencil,
  SendHorizontal,
  Trash2
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../../context/authContext';
import { useAlert } from '../../../context/alertContext';
import { DocumentContext } from '../../../context/documentContext';
import { primaryButtonGreen } from '../../tokenStyle/tokeReset';
import type {
  AnalyzedContractCandidateDisplayEvaluation,
  AnalyzedContractEvaluationCandidate,
  AnalyzedContractImpact
} from '../../../lib/models/interfaces';
import { useUnsavedChangesWarning } from '../rfpUploader/useUnsavedChangesWarning';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import {
  buildNotesDocxBlob,
  type NoteEntry,
  type ParagraphSection
} from './createAnalysisDocument';
import type {
  PdfViewerAnnotationRequest,
  PdfViewerAnnotationReference,
  PdfViewerAnnotationSelection,
  PdfViewerAnnotationStyle,
  SearchablePdfViewerHandle
} from './searchablePdfViewer';
import { PdfFallbackPageItemsByPage } from './search/pdfFallbackSearch';
import { groupPdfFallbackTextItemsByPage } from './search/pdfFallbackSearch-old';
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Popover,
  Select,
  SpaceBetween,
  Spinner
} from './viewerSharedUi';
import { StatusIcon, type StatusIconName } from '../style/StatusIcons';
import { usePolicyContext } from '../../../context/policyContext';

interface CandidateEvaluation {
  candidateId: string;
  contextIds: string[];
  evaluation: AnalyzedContractCandidateDisplayEvaluation;
  cardKey?: string;
  includeInCompanionDocument?: boolean;
  ignoredSignalMetadata?: IgnoredSignalMetadata | null;
  signalId?: string;
  sourcePath?: JsonPathSegment[];
}

interface OutOfScope {
  contextId?: string[];
  contextIds?: string[];
  context_id?: string;
  impact?: AnalyzedContractImpact;
  include_in_companion_document?: boolean;
  includeInCompanionDocument?: boolean;
  ignoredSignalMetadata?: IgnoredSignalMetadata | null;
  reason?: string;
  signal_id?: string;
  sourcePath?: JsonPathSegment[];
  sourceText?: string;
  out_of_scope_id?: string;
  normalized_dimension?: string;
  normalizedDimension?: string;
  source_text?: string;
}

interface IndexedOutOfScopeItem {
  item: OutOfScope;
  index: number;
}

interface IgnoredSignalMetadata {
  ignoredAt?: string;
  ignoredByName?: string;
  ignoredByUserId?: string;
}

interface CandidateNote {
  id: string;
  noteDocumentId: string;
  textId: string;
  text: string;
  owner: string;
  userId: string;
  date: number;
}

interface AnalyzedContractSignalLookup {
  contextIdsBySignalId: Map<string, string[]>;
  contextIdsBySourceText: Map<string, string[]>;
}

interface CompanionDocumentEntry {
  comparisonColor?: string;
  comparisonLabel?: string;
  notes: NoteEntry[];
  originalIndex: number;
  pageNumber: number | string;
  sortMinY: number;
  sortPage: number;
  sortSentenceIndex: number;
  text: string;
}

type JsonPathSegment = string | number;

type AnalyzedContractNoteTargetType = 'candidate' | 'out_of_scope';

interface AnalyzedContractNoteTarget {
  type: AnalyzedContractNoteTargetType;
  id?: string;
  candidateId?: string;
  candidate_id?: string;
  contextId?: string[];
  contextIds?: string[];
  outOfScopeId?: string;
  outOfScopeID?: string;
  out_of_scope_id?: string;
  out_of_scode_id?: string;
  signal_id?: string;
  context_id?: string;
}

interface AnalyzedContractNoteTextUser {
  _id?: string;
  sub?: string;
  name?: string;
  email?: string;
}

interface AnalyzedContractNoteText {
  _id?: string;
  text: string;
  userId?: string | AnalyzedContractNoteTextUser;
  capturedAt?: string | number | Date;
}

interface AnalyzedContractNoteDocument {
  _id: string;
  customerId: string;
  documentId: string;
  target: AnalyzedContractNoteTarget;
  texts: AnalyzedContractNoteText[];
  createdAt?: string;
  updatedAt?: string;
}

interface CreateAnalyzedContractNoteRequest {
  documentId: string;
  target: AnalyzedContractNoteTarget;
  texts: Array<Pick<AnalyzedContractNoteText, 'text'>>;
}

interface CreateCandidateNotePayload {
  documentId: string;
  noteText: string;
  owner: string;
  target: AnalyzedContractNoteTarget;
}

interface UpdateCandidateNotePayload {
  documentId: string;
  noteText_id: string;
  noteId: string;
  text: string;
}

interface DeleteCandidateNotePayload {
  documentId: string;
  _id: string;
  textId: string;
}

const COMPARISON_RESULT_OPTIONS = [
  'stronger_than_benchmark',
  'aligned_with_benchmark',
  'weaker_than_benchmark',
  'missing_benchmarked_element',
  'unclear_or_not_safely_comparable',
  'not_applicable'
] as const;
const NOTES_FILTER = 'notes';
const INCLUDED_SUMMARY_FILTER = 'included_in_summary';
const IGNORED_SIGNALS_FILTER = 'ignored_signals';
const INCLUDE_IN_COMPANION_DOCUMENT_FIELD = 'include_in_companion_document';
const HIGHLIGHT_ALL_BATCH_CACHE_KEY = 'analyzed-contract-highlight-all';
const getHighlightAllPageCacheKey = (pageNumber: number) =>
  `${HIGHLIGHT_ALL_BATCH_CACHE_KEY}::page-${pageNumber}`;
const BASE_RESULT_FILTER_OPTIONS = [
  ...COMPARISON_RESULT_OPTIONS,
  NOTES_FILTER,
  INCLUDED_SUMMARY_FILTER
] as const;

const COMPARISON_NORMALIZED_TEXT: {
  [key: string]: { text: string; color: string };
} = {
  stronger_than_benchmark: { text: 'Not Favorable', color: 'C00000' },
  aligned_with_benchmark: {
    text: 'Favorable and As Expected',
    color: ''
  },
  weaker_than_benchmark: { text: 'Favorable', color: '' },
  missing_benchmarked_element: { text: 'Not Addressed', color: '' },
  unclear_or_not_safely_comparable: {
    text: 'Measured Differently',
    color: ''
  },
  not_applicable: { text: 'Out of Scope', color: '' }
};

type ComparisonResultFilter = (typeof COMPARISON_RESULT_OPTIONS)[number];
type NotesFilter = typeof NOTES_FILTER;
type IncludedSummaryFilter = typeof INCLUDED_SUMMARY_FILTER;
type IgnoredSignalsFilter = typeof IGNORED_SIGNALS_FILTER;
type AnalysisFilter =
  | ComparisonResultFilter
  | NotesFilter
  | IncludedSummaryFilter
  | IgnoredSignalsFilter;

const isComparisonResultFilter = (
  value: string | undefined
): value is ComparisonResultFilter =>
  !!value &&
  COMPARISON_RESULT_OPTIONS.includes(value as ComparisonResultFilter);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const cloneJsonValue = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T;

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

const toBooleanValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const parsedValue = Number(trimmedValue);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => toStringValue(item)).filter(Boolean)
    : [];

const toContextIdArray = (...values: unknown[]): string[] =>
  normalizeContextIds(
    values.flatMap((value) =>
      Array.isArray(value)
        ? value.map((entry) => (typeof entry === 'string' ? entry : null))
        : [typeof value === 'string' ? value : null]
    )
  );

const normalizePageNumbers = (pageNumbers: Array<number | null | undefined>) =>
  Array.from(
    new Set(
      pageNumbers.filter(
        (pageNumber): pageNumber is number =>
          typeof pageNumber === 'number' &&
          Number.isInteger(pageNumber) &&
          pageNumber > 0
      )
    )
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);

const normalizeContextIds = (values: Array<string | null | undefined>) =>
  Array.from(
    new Set(
      values
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean)
    )
  ).sort((leftContextId, rightContextId) =>
    leftContextId.localeCompare(rightContextId)
  );

const normalizeComparableText = (value: unknown) =>
  toStringValue(value).replace(/\s+/g, ' ').trim();

const toImpactValue = (value: unknown): AnalyzedContractImpact | undefined => {
  const normalizedValue = toStringValue(value).trim().toLowerCase();

  switch (normalizedValue) {
    case 'low':
    case 'medium':
    case 'high':
    case 'very high':
      return normalizedValue;
    default:
      return undefined;
  }
};

const looksLikeCandidateDisplayEvaluation = (value: unknown) => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    'comparison_result' in value ||
    'confidence' in value ||
    'basis' in value ||
    'dimension_assessment' in value ||
    'evidence' in value ||
    'watchpoints' in value
  );
};

const toCandidateDisplayEvaluation = (
  value: unknown
): AnalyzedContractCandidateDisplayEvaluation | null => {
  if (!isRecord(value)) {
    return null;
  }

  const basis = isRecord(value.basis) ? value.basis : {};
  const dimensionAssessment = isRecord(value.dimension_assessment)
    ? value.dimension_assessment
    : {};
  const evidence = isRecord(value.evidence) ? value.evidence : {};

  return {
    comparison_result: toStringValue(value.comparison_result),
    confidence: toStringValue(value.confidence),
    impact: toImpactValue(value.impact),
    basis: {
      benchmark_summary: toStringValue(basis.benchmark_summary),
      reason: toStringValue(basis.reason)
    },
    dimension_assessment: {
      benchmark_dimension: toStringValue(
        dimensionAssessment.benchmark_dimension
      ),
      evaluated_dimension: toStringValue(
        dimensionAssessment.evaluated_dimension
      )
    },
    evidence: {
      contextId: toContextIdArray(evidence.contextId, evidence.contextIds),
      evaluated_source_text: toStringValue(evidence.evaluated_source_text)
    },
    watchpoints: toStringArray(value.watchpoints)
  };
};

const toEvaluationCandidate = (
  value: unknown,
  fallbackCandidateId: string
): AnalyzedContractEvaluationCandidate | null => {
  if (!isRecord(value)) {
    return null;
  }

  const nestedEvaluationWrapper = isRecord(value.evaluation_5e)
    ? value.evaluation_5e
    : null;
  const evaluationSource =
    nestedEvaluationWrapper &&
    looksLikeCandidateDisplayEvaluation(nestedEvaluationWrapper.evaluation_5e)
      ? nestedEvaluationWrapper.evaluation_5e
      : looksLikeCandidateDisplayEvaluation(value.evaluation_5e)
        ? value.evaluation_5e
        : looksLikeCandidateDisplayEvaluation(value.evaluation)
          ? value.evaluation
          : looksLikeCandidateDisplayEvaluation(value)
            ? value
            : null;
  const evaluation = toCandidateDisplayEvaluation(evaluationSource);

  if (!evaluation) {
    return null;
  }

  return {
    candidate_id:
      toStringValue(
        value.candidate_id ??
          value.candidateId ??
          nestedEvaluationWrapper?.candidate_id ??
          value.signal_id ??
          value.id
      ) || fallbackCandidateId,
    evaluation_5e: evaluation,
    include_in_companion_document: getIncludeInCompanionDocument(value)
  };
};

const getIncludeInCompanionDocument = (value: unknown): boolean =>
  isRecord(value)
    ? (() => {
        const nestedEvaluationWrapper = isRecord(value.evaluation_5e)
          ? value.evaluation_5e
          : null;

        return toBooleanValue(
          value[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] ??
            value.includeInCompanionDocument ??
            nestedEvaluationWrapper?.[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] ??
            nestedEvaluationWrapper?.includeInCompanionDocument
        );
      })()
    : false;

const getIgnoredSignalMetadata = (
  value: unknown
): IgnoredSignalMetadata | null => {
  if (!isRecord(value)) {
    return null;
  }

  const nestedEvaluationWrapper = isRecord(value.evaluation_5e)
    ? value.evaluation_5e
    : null;
  const ignored = toBooleanValue(
    value.ignored_signal ??
      value.ignoredSignal ??
      nestedEvaluationWrapper?.ignored_signal ??
      nestedEvaluationWrapper?.ignoredSignal
  );

  if (!ignored) {
    return null;
  }

  const ignoredAt = toStringValue(
    value.ignored_at ??
      value.ignoredAt ??
      nestedEvaluationWrapper?.ignored_at ??
      nestedEvaluationWrapper?.ignoredAt
  ).trim();
  const ignoredByName = toStringValue(
    value.ignored_by_name ??
      value.ignoredByName ??
      nestedEvaluationWrapper?.ignored_by_name ??
      nestedEvaluationWrapper?.ignoredByName
  ).trim();
  const ignoredByUserId = toStringValue(
    value.ignored_by_user_id ??
      value.ignoredByUserId ??
      nestedEvaluationWrapper?.ignored_by_user_id ??
      nestedEvaluationWrapper?.ignoredByUserId
  ).trim();

  return {
    ignoredAt: ignoredAt || undefined,
    ignoredByName: ignoredByName || undefined,
    ignoredByUserId: ignoredByUserId || undefined
  };
};

const getEvaluationSignalId = (value: unknown): string => {
  if (!isRecord(value)) {
    return '';
  }

  const evaluationContainer = isRecord(value.evaluation_5e)
    ? isRecord(value.evaluation_5e.evaluation_5e)
      ? value.evaluation_5e
      : value.evaluation_5e
    : isRecord(value.evaluation)
      ? value.evaluation
      : value;

  return normalizeNoteTargetId(
    evaluationContainer.candidate_id ??
      evaluationContainer.candidateId ??
      value.signal_id ??
      value.id
  );
};

const extractCandidateEvaluations = (
  value: unknown,
  path: JsonPathSegment[] = [],
  signalLookup?: AnalyzedContractSignalLookup
): CandidateEvaluation[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) =>
      extractCandidateEvaluations(item, [...path, index], signalLookup)
    );
  }

  if (isRecord(value)) {
    const candidate = toEvaluationCandidate(
      value,
      `Candidate ${path[path.length - 1] ?? 0}`
    );

    if (candidate) {
      const signalId = getEvaluationSignalId(value);
      const normalizedSourceText = normalizeComparableText(
        candidate.evaluation_5e.evidence?.evaluated_source_text
      );
      const directContextIds = toContextIdArray(
        candidate.evaluation_5e.evidence?.contextId
      );
      const fallbackContextIds =
        signalLookup?.contextIdsBySignalId.get(signalId) ||
        (normalizedSourceText
          ? signalLookup?.contextIdsBySourceText.get(normalizedSourceText)
          : undefined) ||
        [];
      const contextIds =
        directContextIds.length > 0
          ? directContextIds
          : fallbackContextIds.length > 0
            ? fallbackContextIds
            : [];

      return [
        {
          candidateId: candidate.candidate_id,
          contextIds,
          evaluation: candidate.evaluation_5e,
          includeInCompanionDocument: candidate.include_in_companion_document,
          ignoredSignalMetadata: getIgnoredSignalMetadata(value),
          signalId,
          sourcePath: path
        }
      ];
    }

    return Object.entries(value).flatMap(([key, entry]) =>
      extractCandidateEvaluations(entry, [...path, key], signalLookup)
    );
  }

  return [];
};

const extractOutOfScopeItems = (
  value: unknown,
  pathPrefix: JsonPathSegment[] = []
): OutOfScope[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item, index) => {
    if (!isRecord(item)) {
      return [];
    }

    return [
      {
        ...(item as OutOfScope),
        contextId: toContextIdArray(
          item.contextId,
          item.contextIds,
          item.context_id
        ),
        include_in_companion_document: getIncludeInCompanionDocument(item),
        ignoredSignalMetadata: getIgnoredSignalMetadata(item),
        sourcePath: [...pathPrefix, index]
      }
    ];
  });
};

const buildSummaryInclusionState = ({
  evaluations,
  outOfScopeItems
}: {
  evaluations: CandidateEvaluation[];
  outOfScopeItems: OutOfScope[];
}) =>
  ({
    ...evaluations.reduce(
      (accumulator, candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        accumulator[cardKey] = !!candidateEvaluation.includeInCompanionDocument;
        return accumulator;
      },
      {} as Record<string, boolean>
    ),
    ...outOfScopeItems.reduce(
      (accumulator, item, index) => {
        const cardKey = getOutOfScopeCardKey(item, index);

        accumulator[cardKey] = !!getIncludeInCompanionDocument(item);
        return accumulator;
      },
      {} as Record<string, boolean>
    )
  }) satisfies Record<string, boolean>;

const buildIgnoredSignalsState = ({
  evaluations,
  outOfScopeItems
}: {
  evaluations: CandidateEvaluation[];
  outOfScopeItems: OutOfScope[];
}) =>
  ({
    ...evaluations.reduce(
      (accumulator, candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        if (candidateEvaluation.ignoredSignalMetadata) {
          accumulator[cardKey] = candidateEvaluation.ignoredSignalMetadata;
        }

        return accumulator;
      },
      {} as Record<string, IgnoredSignalMetadata>
    ),
    ...outOfScopeItems.reduce(
      (accumulator, item, index) => {
        const cardKey = getOutOfScopeCardKey(item, index);

        if (item.ignoredSignalMetadata) {
          accumulator[cardKey] = item.ignoredSignalMetadata;
        }

        return accumulator;
      },
      {} as Record<string, IgnoredSignalMetadata>
    )
  }) satisfies Record<string, IgnoredSignalMetadata>;

const areBooleanRecordsEqual = (
  leftRecord: Record<string, boolean>,
  rightRecord: Record<string, boolean>
) => {
  const recordKeys = new Set([
    ...Object.keys(leftRecord),
    ...Object.keys(rightRecord)
  ]);

  return [...recordKeys].every(
    (recordKey) => !!leftRecord[recordKey] === !!rightRecord[recordKey]
  );
};

const areIgnoredSignalRecordsEqual = (
  leftRecord: Record<string, IgnoredSignalMetadata>,
  rightRecord: Record<string, IgnoredSignalMetadata>
) => {
  const recordKeys = new Set([
    ...Object.keys(leftRecord),
    ...Object.keys(rightRecord)
  ]);

  return [...recordKeys].every((recordKey) => {
    const leftValue = leftRecord[recordKey];
    const rightValue = rightRecord[recordKey];

    return (
      (leftValue?.ignoredAt || '') === (rightValue?.ignoredAt || '') &&
      (leftValue?.ignoredByName || '') === (rightValue?.ignoredByName || '') &&
      (leftValue?.ignoredByUserId || '') === (rightValue?.ignoredByUserId || '')
    );
  });
};

const applyIgnoredSignalMetadataToRecord = (
  record: Record<string, unknown>,
  ignoredSignalMetadata?: IgnoredSignalMetadata
) => {
  if (!ignoredSignalMetadata) {
    record.ignored_signal = false;
    delete record.ignored_at;
    delete record.ignored_by_name;
    delete record.ignored_by_user_id;
    delete record.ignoredSignal;
    delete record.ignoredAt;
    delete record.ignoredByName;
    delete record.ignoredByUserId;
    return;
  }

  record.ignored_signal = true;
  record.ignored_at = ignoredSignalMetadata.ignoredAt || null;
  record.ignored_by_name = ignoredSignalMetadata.ignoredByName || null;
  record.ignored_by_user_id = ignoredSignalMetadata.ignoredByUserId || null;
};

const updateRecordAtPath = (
  rootValue: unknown,
  path: JsonPathSegment[],
  updateRecord: (record: Record<string, unknown>) => void
) => {
  let currentValue: unknown = rootValue;

  for (const segment of path) {
    if (!Array.isArray(currentValue) && !isRecord(currentValue)) {
      return false;
    }

    currentValue = currentValue[segment as keyof typeof currentValue];
  }

  if (!isRecord(currentValue)) {
    return false;
  }

  updateRecord(currentValue);
  return true;
};

const removeEmbeddingFieldsFromCandidates = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => removeEmbeddingFieldsFromCandidates(item));
  }

  if (!isRecord(value)) {
    return value;
  }

  delete value.embedding;
  delete value.embeddingSourceText;

  Object.keys(value).forEach((key) => {
    value[key] = removeEmbeddingFieldsFromCandidates(value[key]);
  });

  return value;
};

const findCollectionByKey = (
  value: unknown,
  targetKey: string,
  pathPrefix: JsonPathSegment[] = []
): { entries: unknown; pathPrefix: JsonPathSegment[] } | null => {
  if (isRecord(value) && targetKey in value) {
    return {
      entries: value[targetKey],
      pathPrefix: [...pathPrefix, targetKey]
    };
  }

  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const nestedMatch = findCollectionByKey(item, targetKey, [
        ...pathPrefix,
        index
      ]);

      if (nestedMatch) {
        return nestedMatch;
      }
    }

    return null;
  }

  if (!isRecord(value)) {
    return null;
  }

  for (const [key, entry] of Object.entries(value)) {
    const nestedMatch = findCollectionByKey(entry, targetKey, [
      ...pathPrefix,
      key
    ]);

    if (nestedMatch) {
      return nestedMatch;
    }
  }

  return null;
};

const getCandidateCollection = (
  value: unknown
): { entries: unknown; pathPrefix: JsonPathSegment[] } =>
  findCollectionByKey(value, 'candidates') ??
  findCollectionByKey(value, 'inScopeSignals') ?? {
    entries: [],
    pathPrefix: ['candidates']
  };

const getOutOfScopeCollection = (
  value: unknown
): { entries: unknown; pathPrefix: JsonPathSegment[] } =>
  findCollectionByKey(value, 'out_of_scope') ??
  findCollectionByKey(value, 'outOfScope') ??
  findCollectionByKey(value, 'outOfScopeItems') ?? {
    entries: [],
    pathPrefix: ['out_of_scope']
  };

const buildBenchmarkRuleIdBySignalId = (
  value: unknown
): Record<string, string> => {
  if (!Array.isArray(value)) {
    return {};
  }

  return value.reduce(
    (benchmarkRuleIdBySignalId, entry) => {
      if (!isRecord(entry)) {
        return benchmarkRuleIdBySignalId;
      }

      const signalId = toStringValue(entry.signal_id).trim();
      const benchmarkRuleId = toStringValue(
        entry.benchmark_rule_id ?? entry.matched_candidate_id
      ).trim();

      if (signalId && benchmarkRuleId) {
        benchmarkRuleIdBySignalId[signalId] = benchmarkRuleId;
      }

      return benchmarkRuleIdBySignalId;
    },
    {} as Record<string, string>
  );
};

const buildAnalyzedContractSignalLookup = (
  value: unknown
): AnalyzedContractSignalLookup => {
  const contextIdsBySignalId = new Map<string, string[]>();
  const contextIdsBySourceText = new Map<string, string[]>();
  const signalCollection = findCollectionByKey(value, 'inScopeSignals');
  const signalEntries = signalCollection?.entries;

  if (!Array.isArray(signalEntries)) {
    return {
      contextIdsBySignalId,
      contextIdsBySourceText
    };
  }

  signalEntries.forEach((entry) => {
    if (!isRecord(entry)) {
      return;
    }

    const contextIds = toContextIdArray(
      entry.contextId,
      entry.contextIds,
      entry.context_id
    );

    if (contextIds.length === 0) {
      return;
    }

    const signalId = normalizeNoteTargetId(entry.signal_id);
    const normalizedSourceText = normalizeComparableText(
      entry.source_text ?? entry.sourceText
    );

    if (signalId) {
      contextIdsBySignalId.set(signalId, contextIds);
    }

    if (normalizedSourceText) {
      contextIdsBySourceText.set(normalizedSourceText, contextIds);
    }
  });

  return {
    contextIdsBySignalId,
    contextIdsBySourceText
  };
};

const renderValue = (value: string | undefined) =>
  value && value.trim() ? value : 'N/A';

const getOutOfScopeContextIds = (item: OutOfScope): string[] =>
  toContextIdArray(item.contextId, item.contextIds, item.context_id);

const getPrimaryContextId = (
  contextIds: Array<string | null | undefined>
): string | undefined => normalizeContextIds(contextIds)[0] || undefined;

const hasMatchingContextId = (
  leftContextIds: Array<string | null | undefined>,
  rightContextIds: Array<string | null | undefined>
) => {
  const normalizedLeft = normalizeContextIds(leftContextIds);
  const normalizedRight = new Set(normalizeContextIds(rightContextIds));

  return normalizedLeft.some((contextId) => normalizedRight.has(contextId));
};

const getOutOfScopeCardKey = (item: OutOfScope, index: number): string =>
  item.signal_id ||
  item.out_of_scope_id ||
  getOutOfScopeContextIds(item).join('|') ||
  `out-of-scope-${index}`;

const getOutOfScopeTargetId = (item: OutOfScope, fallbackKey: string): string =>
  item.out_of_scope_id ||
  item.signal_id ||
  getOutOfScopeContextIds(item).join('|') ||
  fallbackKey;

const normalizeNoteTargetId = (value: unknown): string =>
  toStringValue(value).trim();

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const isSiblingTaskAbortError = (error: unknown) => {
  if (error instanceof Error) {
    return error.message.includes('Sibling task failed');
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  return (
    'type' in error &&
    'reason' in error &&
    error.type === 'abort' &&
    error.reason === 'Sibling task failed'
  );
};

const getNoteTargetType = (
  target: AnalyzedContractNoteTarget
): AnalyzedContractNoteTargetType | '' => {
  const targetType = normalizeNoteTargetId(target.type).toLowerCase();

  if (targetType === 'candidate') {
    return 'candidate';
  }

  if (
    targetType === 'out_of_scope' ||
    targetType === 'outofscope' ||
    targetType === 'out-of-scope'
  ) {
    return 'out_of_scope';
  }

  return '';
};

const getNoteTargetCandidateId = (target: AnalyzedContractNoteTarget): string =>
  normalizeNoteTargetId(target.candidateId || target.candidate_id || target.id);

const getNoteTargetOutOfScopeId = (
  target: AnalyzedContractNoteTarget
): string =>
  normalizeNoteTargetId(
    (() => {
      const contextIds = getNoteTargetContextIds(target);

      return (
        target.outOfScopeId ||
        target.outOfScopeID ||
        target.out_of_scope_id ||
        target.out_of_scode_id ||
        target.signal_id ||
        contextIds.join('|') ||
        target.context_id ||
        target.id
      );
    })()
  );

const getNoteTargetContextIds = (
  target: AnalyzedContractNoteTarget
): string[] =>
  toContextIdArray(target.contextId, target.contextIds, target.context_id);

const getNotesForTarget = ({
  notesByTargetId,
  targetId,
  fallbackTargetIds = []
}: {
  notesByTargetId: Record<string, CandidateNote[]>;
  targetId: string;
  fallbackTargetIds?: Array<string | undefined>;
}): CandidateNote[] => {
  const lookupKeys = [targetId, ...fallbackTargetIds]
    .map((lookupKey) => normalizeNoteTargetId(lookupKey))
    .filter(Boolean);
  const seenNoteIds = new Set<string>();

  return lookupKeys.flatMap((lookupKey) =>
    (notesByTargetId[lookupKey] || []).filter((note) => {
      if (seenNoteIds.has(note.id)) {
        return false;
      }

      seenNoteIds.add(note.id);
      return true;
    })
  );
};

const isAnalyzedContractNoteDocument = (
  value: unknown
): value is AnalyzedContractNoteDocument =>
  isRecord(value) && isRecord(value.target) && Array.isArray(value.texts);

const extractAnalyzedContractNoteDocuments = (
  value: unknown
): AnalyzedContractNoteDocument[] => {
  if (isAnalyzedContractNoteDocument(value)) {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => extractAnalyzedContractNoteDocuments(item));
  }

  if (!isRecord(value)) {
    return [];
  }

  const directCollectionKeys = [
    'result',
    'results',
    'notes',
    'note',
    'data',
    'items',
    'documents',
    'body'
  ];

  for (const key of directCollectionKeys) {
    const nestedValue = value[key];
    const nestedNotes = extractAnalyzedContractNoteDocuments(nestedValue);

    if (nestedNotes.length > 0) {
      return nestedNotes;
    }
  }

  return Object.values(value).flatMap((nestedValue) =>
    extractAnalyzedContractNoteDocuments(nestedValue)
  );
};

const getNoteTextOwner = (
  noteText: AnalyzedContractNoteText,
  fallbackOwner: string
) => {
  if (typeof noteText.userId === 'object' && noteText.userId !== null) {
    return noteText.userId.name || noteText.userId.email || fallbackOwner;
  }

  return fallbackOwner;
};

const getNoteTextUserId = (noteText: AnalyzedContractNoteText): string => {
  if (typeof noteText.userId === 'string') {
    return noteText.userId;
  }

  if (typeof noteText.userId === 'object' && noteText.userId !== null) {
    return noteText.userId._id || noteText.userId.sub || '';
  }

  return '';
};

const getNoteTextDate = (noteText: AnalyzedContractNoteText) => {
  const capturedAt = noteText.capturedAt
    ? new Date(noteText.capturedAt).getTime()
    : Date.now();

  return Number.isFinite(capturedAt) ? capturedAt : Date.now();
};

const toCandidateNote = (
  noteText: AnalyzedContractNoteText,
  fallbackOwner: string,
  noteDocumentId = ''
): CandidateNote => {
  const textId =
    noteText._id || `${noteText.text}-${getNoteTextDate(noteText)}`;

  return {
    id: textId,
    noteDocumentId,
    textId,
    text: noteText.text,
    owner: getNoteTextOwner(noteText, fallbackOwner),
    userId: getNoteTextUserId(noteText),
    date: getNoteTextDate(noteText)
  };
};
const normalizeNoteTarget = (
  target: AnalyzedContractNoteTarget
): AnalyzedContractNoteTarget => {
  const normalizedType = getNoteTargetType(target);

  if (normalizedType === 'candidate') {
    return {
      type: 'candidate',
      candidateId: getNoteTargetCandidateId(target),
      contextIds: getNoteTargetContextIds(target)
    };
  }

  if (normalizedType === 'out_of_scope') {
    return {
      type: 'out_of_scope',
      outOfScopeId: getNoteTargetOutOfScopeId(target),
      contextIds: getNoteTargetContextIds(target)
    };
  }

  return target;
};

const extractCreatedCandidateNote = (
  responseData: unknown,
  fallbackText: string,
  fallbackOwner: string
): CandidateNote => {
  const data = isRecord(responseData) ? responseData : {};
  const result = data.result ?? data.note ?? data;
  const resultValue = Array.isArray(result) ? result[0] : result;

  if (isRecord(resultValue) && Array.isArray(resultValue.texts)) {
    const createdText = resultValue.texts[resultValue.texts.length - 1] as
      | AnalyzedContractNoteText
      | undefined;

    if (createdText?.text) {
      return toCandidateNote(
        createdText,
        fallbackOwner,
        toStringValue(resultValue._id)
      );
    }
  }

  if (isRecord(resultValue) && typeof resultValue.text === 'string') {
    return toCandidateNote(
      resultValue as unknown as AnalyzedContractNoteText,
      fallbackOwner
    );
  }

  const fallbackId = `${fallbackText}-${Date.now()}`;

  return {
    id: fallbackId,
    noteDocumentId: '',
    textId: fallbackId,
    text: fallbackText,
    owner: fallbackOwner,
    userId: '',
    date: Date.now()
  };
};

const getOutOfScopeFallbackTargetIds = (
  item: OutOfScope,
  index: number
): Array<string | undefined> => [
  getOutOfScopeCardKey(item, index),
  item.out_of_scope_id,
  item.signal_id,
  ...getOutOfScopeContextIds(item)
];

const buildCandidateNotesByTargetId = ({
  noteDocuments,
  fallbackOwner
}: {
  noteDocuments: AnalyzedContractNoteDocument[];
  fallbackOwner: string;
}): Record<string, CandidateNote[]> => {
  const notesByTargetId: Record<string, CandidateNote[]> = {};

  noteDocuments.forEach((noteDocument) => {
    const targetType = getNoteTargetType(noteDocument.target);
    const targetId =
      targetType === 'candidate'
        ? getNoteTargetCandidateId(noteDocument.target)
        : getNoteTargetOutOfScopeId(noteDocument.target);

    if (!targetType || !targetId) {
      return;
    }

    const notes = noteDocument.texts
      .filter((noteText) => noteText.text?.trim())
      .map((noteText) =>
        toCandidateNote(noteText, fallbackOwner, noteDocument._id)
      );

    notesByTargetId[targetId] = [
      ...(notesByTargetId[targetId] || []),
      ...notes
    ].sort((firstNote, secondNote) => firstNote.date - secondNote.date);
  });

  return notesByTargetId;
};

const createCandidateNote = async (
  payload: CreateCandidateNotePayload,
  auth: { headers: { Authorization: string } }
): Promise<CandidateNote> => {
  if (!auth.headers.Authorization) {
    throw new Error('Missing authorization for note creation.');
  }

  const requestBody: CreateAnalyzedContractNoteRequest = {
    documentId: payload.documentId,
    target: normalizeNoteTarget(payload.target),
    texts: [{ text: payload.noteText }]
  };

  const response = await axios.post<AnalyzedContractNoteDocument>(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=addNew`,
    requestBody,
    auth
  );

  return extractCreatedCandidateNote(
    response.data,
    payload.noteText,
    payload.owner
  );
};

const updateCandidateNoteText = async (
  payload: UpdateCandidateNotePayload,
  auth: { headers: { Authorization: string } }
) => {
  if (!auth.headers.Authorization) {
    throw new Error('Missing authorization for note update.');
  }

  await axios.post(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=update`,
    payload,
    auth
  );
};

const deleteCandidateNoteText = async (
  payload: DeleteCandidateNotePayload,
  auth: { headers: { Authorization: string } }
) => {
  if (!auth.headers.Authorization) {
    throw new Error('Missing authorization for note deletion.');
  }

  await axios.post(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=remove`,
    payload,
    auth
  );
};

const getCandidateNotesForDocument = async ({
  documentId,
  auth,
  fallbackOwner
}: {
  documentId: string;
  auth: { headers: { Authorization: string } };
  fallbackOwner: string;
}): Promise<Record<string, CandidateNote[]>> => {
  const response = await axios.post(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=getAllForDocument`,
    { documentId },
    auth
  );

  return buildCandidateNotesByTargetId({
    noteDocuments: extractAnalyzedContractNoteDocuments(response.data),
    fallbackOwner
  });
};

const renderWatchPoints = (value: string[] | undefined) => {
  if (!value || value.length === 0) {
    return null;
  }
  return (
    <ul className="list-disc space-y-1 pl-5">
      {value.map((watchpoint) => (
        <li key={watchpoint} className="list-item">
          {watchpoint}
        </li>
      ))}
    </ul>
  );
};

const renderReadOnlyField = (
  label: string,
  content: ReactNode,
  className = 'border-gray-300 bg-gray-50'
) => (
  <FormField label={label}>
    <Box
      padding="m"
      className={`rounded border whitespace-pre-wrap ${className}`}
    >
      {content}
    </Box>
  </FormField>
);

const formatResolvedPageNumbersLabel = (pageNumbers: number[]) =>
  pageNumbers.length > 0 ? pageNumbers.join(', ') : 'No matching page';

const formatContextIdsLabel = (
  contextIds: Array<string | null | undefined>
) => {
  const normalizedContextIds = normalizeContextIds(contextIds);

  return normalizedContextIds.length > 0
    ? normalizedContextIds.join(', ')
    : 'No context IDs';
};

const renderCardSourceMetadata = ({
  contextIds,
  pageNumbers,
  benchmarkRuleId,
  showContextIds = false
}: {
  contextIds: Array<string | null | undefined>;
  pageNumbers: number[];
  benchmarkRuleId?: string | null;
  showContextIds?: boolean;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-600">
    <div className="font-semibold uppercase tracking-[0.14em] text-slate-500">
      Source Metadata
    </div>
    <div className="mt-2 space-y-1">
      <div>
        <span className="font-semibold text-slate-700">Pages:</span>{' '}
        {formatResolvedPageNumbersLabel(pageNumbers)}
      </div>
      {benchmarkRuleId ? (
        <div className="break-all">
          <span className="font-semibold text-slate-700">
            Benchmark Rule ID:
          </span>{' '}
          {benchmarkRuleId}
        </div>
      ) : null}
      {showContextIds ? (
        <div className="break-all">
          <span className="font-semibold text-slate-700">Context IDs:</span>{' '}
          {formatContextIdsLabel(contextIds)}
        </div>
      ) : null}
    </div>
  </div>
);

const formatComparisonResultLabel = (value: string) =>
  COMPARISON_NORMALIZED_TEXT[value]?.text ?? value;

const getComparisonResultStatusIconName = (
  value: string | undefined | null
): StatusIconName | null => {
  switch (value) {
    case 'stronger_than_benchmark':
      return 'Not Favorable';
    case 'aligned_with_benchmark':
      return 'Favorable and as Expected';
    case 'weaker_than_benchmark':
      return 'Favorable';
    case 'missing_benchmarked_element':
      return 'Not addressed';
    case 'unclear_or_not_safely_comparable':
      return 'Measured Differently';
    case 'not_applicable':
      return 'Out of Scope';
    default:
      return null;
  }
};

const formatConfidenceLabel = (value: string | null | undefined) =>
  value
    ? `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()} confidence`
    : '';

const renderComparisonResultHeaderContent = (
  comparisonResult: string | undefined | null,
  confidence?: string | null,
  fallbackTitle?: string
) => {
  const iconName = getComparisonResultStatusIconName(comparisonResult);
  const comparisonLabel = comparisonResult
    ? formatComparisonResultLabel(comparisonResult)
    : fallbackTitle || '';
  const confidenceLabel = formatConfidenceLabel(confidence);

  if (!iconName) {
    if (!comparisonLabel && !fallbackTitle) {
      return null;
    }

    return (
      <span className="inline-flex flex-wrap items-center gap-3">
        {comparisonLabel || fallbackTitle ? (
          <span>{comparisonLabel || fallbackTitle}</span>
        ) : null}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-3">
      <Popover
        content={comparisonLabel}
        offsetX={12}
        position="top"
        size="small"
      >
        <span className="inline-flex">
          <StatusIcon name={iconName} size={22} className="shrink-0" />
        </span>
      </Popover>
      <span className="sr-only">{comparisonLabel}</span>
      {confidenceLabel ? (
        <span className="text-xs font-medium uppercase tracking-[0.14em] text-slate-500">
          {confidenceLabel}
        </span>
      ) : null}
    </span>
  );
};

const renderComparisonResultFilterLabel = (
  comparisonResult: ComparisonResultFilter
) => {
  const iconName = getComparisonResultStatusIconName(comparisonResult);
  const comparisonLabel = formatComparisonResultLabel(comparisonResult);

  return (
    <span className="inline-flex items-center gap-2">
      {iconName ? (
        <StatusIcon
          name={iconName}
          size={18}
          className="shrink-0"
          title={comparisonLabel}
        />
      ) : null}
      <span>{comparisonLabel}</span>
    </span>
  );
};

const formatComparisonResultColor = (value: string) =>
  COMPARISON_NORMALIZED_TEXT[value]?.color || undefined;

const formatAnalysisFilterLabel = (value: AnalysisFilter) => {
  if (value === NOTES_FILTER) {
    return 'Notes';
  }

  if (value === INCLUDED_SUMMARY_FILTER) {
    return 'Included in Summary';
  }

  if (value === IGNORED_SIGNALS_FILTER) {
    return 'Ignored Signals';
  }

  return formatComparisonResultLabel(value);
};

const getOutOfScopeSourceText = (item: OutOfScope) =>
  item.source_text || item.sourceText || '';

const normalizeViewerSearchText = (value: unknown) =>
  normalizeComparableText(value).toLowerCase();

const matchesViewerSearchText = (
  value: unknown,
  normalizedSearchText: string
) =>
  !normalizedSearchText ||
  normalizeViewerSearchText(value).includes(normalizedSearchText);

const matchesCandidateViewerSearch = (
  candidateEvaluation: CandidateEvaluation,
  normalizedSearchText: string
) =>
  !normalizedSearchText ||
  matchesViewerSearchText(candidateEvaluation.signalId, normalizedSearchText) ||
  matchesViewerSearchText(
    candidateEvaluation.evaluation.evidence?.evaluated_source_text,
    normalizedSearchText
  );

const matchesOutOfScopeViewerSearch = (
  item: OutOfScope,
  normalizedSearchText: string
) =>
  !normalizedSearchText ||
  matchesViewerSearchText(item.signal_id, normalizedSearchText) ||
  matchesViewerSearchText(getOutOfScopeSourceText(item), normalizedSearchText);

const getCandidateCardKey = (
  candidateEvaluation: CandidateEvaluation,
  index: number
) =>
  candidateEvaluation.cardKey || `${candidateEvaluation.candidateId}-${index}`;

const buildCandidateAnnotationReference = (
  candidateEvaluation: CandidateEvaluation,
  index: number,
  pageNumbers?: number[]
): PdfViewerAnnotationReference => ({
  candidateCardKey: getCandidateCardKey(candidateEvaluation, index),
  candidateId: candidateEvaluation.candidateId.trim() || undefined,
  contextId: getPrimaryContextId(candidateEvaluation.contextIds),
  contextIds: candidateEvaluation.contextIds,
  pageNumbers,
  sourceText:
    candidateEvaluation.evaluation.evidence?.evaluated_source_text.trim() ||
    undefined
});

const buildOutOfScopeAnnotationReference = (
  item: OutOfScope,
  index: number,
  pageNumbers?: number[]
): PdfViewerAnnotationReference => ({
  candidateCardKey: getOutOfScopeCardKey(item, index),
  contextId: getPrimaryContextId(getOutOfScopeContextIds(item)),
  contextIds: getOutOfScopeContextIds(item),
  pageNumbers,
  sourceText: getOutOfScopeSourceText(item).trim() || undefined
});

const buildHighlightAllBatchSignature = (
  requests: PdfViewerAnnotationRequest[]
) =>
  JSON.stringify(
    requests.map((request) => ({
      candidateCardKey: request.reference?.candidateCardKey ?? '',
      contextId: request.reference?.contextId ?? '',
      contextIds: request.reference?.contextIds ?? [],
      pageNumber: request.pageNumber ?? null,
      pageNumbers: request.pageNumbers ?? [],
      query: request.query
    }))
  );

const toContextPageNumberMap = (value: unknown): Map<string, number[]> => {
  const nextMap = new Map<string, number[]>();

  const assignPageNumbers = (contextIdValue: unknown, pageValue: unknown) => {
    const normalizedPageValue = isRecord(pageValue)
      ? (pageValue.pageNumbers ??
        pageValue.page_numbers ??
        pageValue.pages ??
        pageValue.pageNumber ??
        pageValue.page ??
        pageValue.page_index)
      : pageValue;
    const pageNumbers = normalizePageNumbers(
      Array.isArray(normalizedPageValue)
        ? normalizedPageValue.map((entry) => toFiniteNumber(entry))
        : [toFiniteNumber(normalizedPageValue)]
    );

    if (pageNumbers.length === 0) {
      return;
    }

    normalizeContextIds(
      Array.isArray(contextIdValue)
        ? contextIdValue.map((entry) =>
            typeof entry === 'string' ? entry : null
          )
        : [typeof contextIdValue === 'string' ? contextIdValue : null]
    ).forEach((contextId) => {
      nextMap.set(
        contextId,
        normalizePageNumbers([
          ...(nextMap.get(contextId) ?? []),
          ...pageNumbers
        ])
      );
    });
  };

  const sourceValue =
    isRecord(value) && isRecord(value.contextPageMap)
      ? value.contextPageMap
      : value;

  if (Array.isArray(sourceValue)) {
    sourceValue.forEach((entry) => {
      if (!isRecord(entry)) {
        return;
      }

      assignPageNumbers(
        entry.contextIds ?? entry.contextId,
        entry.pageNumbers ??
          entry.page_numbers ??
          entry.pages ??
          entry.pageNumber ??
          entry.page ??
          entry.page_index
      );
    });

    return nextMap;
  }

  if (!isRecord(sourceValue)) {
    return nextMap;
  }

  Object.entries(sourceValue).forEach(([contextId, pageValue]) => {
    assignPageNumbers(contextId, pageValue);
  });

  return nextMap;
};

const getPageNumberForContextIds = (
  contextIds: Array<string | null | undefined>,
  contextPageMap: Map<string, number[]>
) =>
  normalizeContextIds(contextIds)
    .flatMap((contextId) => contextPageMap.get(contextId) ?? [])
    .find((pageNumber): pageNumber is number => pageNumber !== null);

const getPageNumbersForContextIds = (
  contextIds: Array<string | null | undefined>,
  contextPageMap: Map<string, number[]>
) =>
  Array.from(
    new Set(
      normalizeContextIds(contextIds)
        .flatMap((contextId) => contextPageMap.get(contextId) ?? [])
        .filter((pageNumber): pageNumber is number => pageNumber !== null)
    )
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);

const getResolvedPageNumbers = ({
  contextIds,
  contextPageMap
}: {
  contextIds: Array<string | null | undefined>;
  contextPageMap: Map<string, number[]>;
}) => getPageNumbersForContextIds(contextIds, contextPageMap);

const formatMissingResolvedPageMessage = (
  contextIds: Array<string | null | undefined>
) => {
  const normalizedContextIds = normalizeContextIds(contextIds);

  return `${
    normalizedContextIds.length > 0
      ? normalizedContextIds.join(', ')
      : 'Unknown contextId'
  } NO matching page`;
};

const buildCompanionNoteEntries = (notes: CandidateNote[]) =>
  notes.map((note) => ({
    text: note.text,
    createdBy: note.owner || 'Unknown user',
    createdOn: new Date(note.date)
  }));

const buildCandidateCompanionText = ({
  evaluation
}: {
  evaluation: AnalyzedContractCandidateDisplayEvaluation;
}) => renderValue(evaluation.evidence?.evaluated_source_text);

const buildOutOfScopeCompanionText = ({ item }: { item: OutOfScope }) =>
  renderValue(getOutOfScopeSourceText(item));

const getCompanionDocumentBaseName = (
  fileName: string | undefined,
  fallback: string
) => {
  const normalizedFileName = toStringValue(fileName).trim();
  const baseName = normalizedFileName.replace(/\.[^/.]+$/, '').trim();

  return baseName || fallback;
};

const sanitizeCompanionDocumentFileName = (value: string) =>
  value
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getDimensionGroupValues = (
  evaluation: AnalyzedContractCandidateDisplayEvaluation
) => {
  const benchmarkDimension =
    evaluation.dimension_assessment?.benchmark_dimension?.trim() ||
    'Unspecified benchmark dimension';
  const evaluatedDimension =
    evaluation.dimension_assessment?.evaluated_dimension?.trim() ||
    'Unspecified evaluated dimension';

  return {
    benchmarkDimension,
    evaluatedDimension,
    groupKey: `${benchmarkDimension}|||${evaluatedDimension}`
  };
};

const buildDimensionGroups = (candidateEvaluations: CandidateEvaluation[]) => {
  const groups = candidateEvaluations.reduce(
    (accumulator, candidateEvaluation, index) => {
      const { benchmarkDimension, evaluatedDimension, groupKey } =
        getDimensionGroupValues(candidateEvaluation.evaluation);

      if (!accumulator[groupKey]) {
        accumulator[groupKey] = {
          benchmarkDimension,
          evaluatedDimension,
          items: []
        };
      }

      accumulator[groupKey].items.push({
        ...candidateEvaluation,
        index
      });

      return accumulator;
    },
    {} as Record<
      string,
      {
        benchmarkDimension: string;
        evaluatedDimension: string;
        items: Array<CandidateEvaluation & { index: number }>;
      }
    >
  );

  return Object.values(groups);
};

const getOutOfScopeDimensionValue = (item: OutOfScope): string =>
  toStringValue(item.normalized_dimension ?? item.normalizedDimension).trim() ||
  'Unspecified dimension';

const getOutOfScopeDimensionGroupKey = (item: OutOfScope): string =>
  `out_of_scope|||${getOutOfScopeDimensionValue(item)}`;

const buildOutOfScopeDimensionGroups = (entries: IndexedOutOfScopeItem[]) => {
  const groups = entries.reduce(
    (accumulator, entry) => {
      const normalizedDimension = getOutOfScopeDimensionValue(entry.item);
      const groupKey = getOutOfScopeDimensionGroupKey(entry.item);

      if (!accumulator[groupKey]) {
        accumulator[groupKey] = {
          groupKey,
          normalizedDimension,
          items: []
        };
      }

      accumulator[groupKey].items.push(entry);
      return accumulator;
    },
    {} as Record<
      string,
      {
        groupKey: string;
        normalizedDimension: string;
        items: IndexedOutOfScopeItem[];
      }
    >
  );

  return Object.values(groups);
};

const formatDimensionDisplayLabel = (value: string): string =>
  value
    .trim()
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const formatDimensionGroupDisplayLabel = (
  benchmarkDimension: string,
  evaluatedDimension: string
): string => {
  const benchmarkLabel = formatDimensionDisplayLabel(benchmarkDimension);
  const evaluatedLabel = formatDimensionDisplayLabel(evaluatedDimension);

  if (!benchmarkLabel) {
    return evaluatedLabel;
  }

  if (!evaluatedLabel || benchmarkLabel === evaluatedLabel) {
    return benchmarkLabel;
  }

  return `${benchmarkLabel} / ${evaluatedLabel}`;
};

const getComparisonResultCardClasses = (
  result: ComparisonResultFilter,
  isActive: boolean
) => {
  const activeState = isActive
    ? 'ring-1 ring-inset ring-black/5 shadow-sm'
    : 'opacity-90 hover:opacity-100 hover:-translate-y-0.5';

  switch (result) {
    case 'stronger_than_benchmark':
      return `border-red-300 bg-red-50 text-red-900 ${activeState}`;
    case 'aligned_with_benchmark':
      return `border-sky-300 bg-sky-50 text-sky-900 ${activeState}`;
    case 'weaker_than_benchmark':
      return `border-emerald-300 bg-emerald-50 text-emerald-900 ${activeState}`;
    case 'missing_benchmarked_element':
      return `border-amber-300 bg-amber-50 text-amber-900 ${activeState}`;
    case 'unclear_or_not_safely_comparable':
      return `border-violet-300 bg-violet-50 text-violet-900 ${activeState}`;
    case 'not_applicable':
      return `border-slate-300 bg-slate-100 text-slate-900 ${activeState}`;
    default:
      return `border-slate-300 bg-white text-slate-900 ${activeState}`;
  }
};

const formatImpactLabel = (impact: AnalyzedContractImpact | undefined) =>
  impact
    ? `${impact
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')} Impact`
    : '';

const getAnalysisFilterCardClasses = (
  result: AnalysisFilter,
  isActive: boolean
) => {
  const activeState = isActive
    ? 'ring-1 ring-inset ring-black/5 shadow-sm'
    : 'opacity-90 hover:opacity-100 hover:-translate-y-0.5';

  if (result === NOTES_FILTER) {
    return `border-[#56dac5] bg-[#e9fbf8] text-[#006f62] ${activeState}`;
  }

  if (result === INCLUDED_SUMMARY_FILTER) {
    return `border-emerald-300 bg-emerald-50 text-emerald-900 ${activeState}`;
  }

  if (result === IGNORED_SIGNALS_FILTER) {
    return `border-rose-300 bg-rose-50 text-rose-900 ${activeState}`;
  }

  return getComparisonResultCardClasses(result, isActive);
};

const handleInteractiveCardKeyDown = (
  event: KeyboardEvent<HTMLDivElement>,
  onActivate: () => void
) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  event.preventDefault();
  onActivate();
};

type NoteActionsMenuPosition = {
  top: number;
  left: number;
};

const FloatingNoteActionsMenu = ({
  anchorRect,
  isDeleting,
  onEdit,
  onDelete,
  onClose
}: {
  anchorRect: DOMRect | null;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<NoteActionsMenuPosition | null>(
    null
  );

  useLayoutEffect(() => {
    if (!anchorRect) {
      return;
    }

    const updatePosition = () => {
      const menuWidth = menuRef.current?.offsetWidth || 176;
      const menuHeight = menuRef.current?.offsetHeight || 96;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;

      let left = anchorRect.right - menuWidth;
      let top = anchorRect.bottom + margin;

      if (left < margin) {
        left = margin;
      }

      if (left + menuWidth > viewportWidth - margin) {
        left = viewportWidth - menuWidth - margin;
      }

      if (top + menuHeight > viewportHeight - margin) {
        top = anchorRect.top - menuHeight - margin;
      }

      if (top < margin) {
        top = margin;
      }

      setPosition({ top, left });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRect]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (menuRef.current?.contains(target)) {
        return;
      }

      onClose();
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!anchorRect || !position) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-2xl ring-1 ring-black/5"
      style={{ top: position.top, left: position.left }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 transition hover:bg-slate-50"
        onClick={() => {
          onEdit();
          onClose();
        }}
      >
        <Pencil size={14} />
        <span>Edit note</span>
      </button>
      <button
        type="button"
        disabled={isDeleting}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onDelete();
          onClose();
        }}
      >
        <Trash2 size={14} />
        <span>{isDeleting ? 'Deleting...' : 'Delete note'}</span>
      </button>
    </div>,
    document.body
  );
};

const CandidateNotesPanel = ({
  notes,
  noteValue,
  isPosting,
  showComposer,
  currentUserId,
  openActionsNoteId,
  editingNoteId,
  editNoteValue,
  updatingNoteId,
  deletingNoteId,
  openActionsAnchorRect,
  onCloseActions,
  notePanelRef,
  onNoteChange,
  onPost,
  onToggleActions,
  onStartEdit,
  onEditNoteChange,
  onCancelEdit,
  onSaveEdit,
  onDelete
}: {
  notes: CandidateNote[];
  noteValue: string;
  isPosting: boolean;
  showComposer: boolean;
  currentUserId: string;
  openActionsNoteId: string | null;
  editingNoteId: string | null;
  editNoteValue: string;
  updatingNoteId: string | null;
  deletingNoteId: string | null;
  openActionsAnchorRect: DOMRect | null;
  onCloseActions: () => void;
  notePanelRef?: (node: HTMLDivElement | null) => void;
  onNoteChange: (value: string) => void;
  onPost: () => void;
  onToggleActions: (noteId: string, anchorRect: DOMRect) => void;
  onStartEdit: (note: CandidateNote) => void;
  onEditNoteChange: (value: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (note: CandidateNote) => void;
  onDelete: (note: CandidateNote) => void;
}) => {
  const hasNoteText = noteValue.trim().length > 0;
  const lastNoteId = notes[notes.length - 1]?.id;

  return (
    <div
      ref={notePanelRef}
      className="rounded-xl border border-slate-200 bg-white/90 p-4 text-slate-900 shadow-sm ring-1 ring-black/5"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Notes
        </div>
        <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
          {notes.length}
        </div>
      </div>

      {notes.length > 0 ? (
        <div className="mt-3 max-h-40 space-y-2 overflow-y-auto pr-1">
          {notes.map((note) => {
            const isLastNote = note.id === lastNoteId;
            const canManageNote =
              isLastNote &&
              !!currentUserId &&
              note.userId === currentUserId &&
              !!note.noteDocumentId &&
              !!note.textId;
            const isEditing = editingNoteId === note.id;
            const isUpdating = updatingNoteId === note.id;
            const isDeleting = deletingNoteId === note.id;
            const isActionsOpen = openActionsNoteId === note.id;

            return (
              <div
                key={note.id}
                className="relative rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editNoteValue}
                      rows={3}
                      className="min-h-[84px] w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-5 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                      onChange={(event) => onEditNoteChange(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-white"
                        onClick={onCancelEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!editNoteValue.trim() || isUpdating}
                        className="rounded-full border border-[#56dac5] bg-[#56dac5] px-3 py-1 text-xs font-semibold text-white transition hover:border-[#00a28f] hover:bg-[#00a28f] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
                        onClick={() => onSaveEdit(note)}
                      >
                        {isUpdating ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pr-10 text-sm leading-5 text-slate-800">
                    {note.text}
                  </div>
                )}

                <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-200 pt-2 text-xs text-slate-500">
                  <span>{note.owner}</span>
                  <div className="flex items-center gap-2">
                    <span>{new Date(note.date).toLocaleDateString()}</span>
                    {canManageNote && !isEditing ? (
                      <>
                        <button
                          type="button"
                          aria-label="More actions"
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#56dac5]/40"
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleActions(
                              note.id,
                              event.currentTarget.getBoundingClientRect()
                            );
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {isActionsOpen ? (
                          <FloatingNoteActionsMenu
                            anchorRect={openActionsAnchorRect}
                            isDeleting={isDeleting}
                            onEdit={() => onStartEdit(note)}
                            onDelete={() => onDelete(note)}
                            onClose={onCloseActions}
                          />
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {showComposer ? (
        <div className="mt-3 flex items-start gap-2">
          <textarea
            value={noteValue}
            rows={3}
            className="min-h-[84px] flex-1 resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-5 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            placeholder="Add a note..."
            onChange={(event) => onNoteChange(event.target.value)}
          />
          <button
            type="button"
            aria-label="Post note"
            disabled={!hasNoteText || isPosting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#56dac5] bg-[#56dac5] text-white shadow-sm transition hover:border-[#00a28f] hover:bg-[#00a28f] focus:outline-none focus:ring-2 focus:ring-[#56dac5]/40 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
            onClick={(event) => {
              event.currentTarget.blur();
              onPost();
            }}
          >
            {isPosting ? <Spinner /> : <SendHorizontal size={18} />}
          </button>
        </div>
      ) : null}
    </div>
  );
};

interface AnalyzedContractViewerProps {
  currentPdfPage?: number | null;
  onLoadingStateChange?: (isLoading: boolean) => void;
  selectedAnnotationReference?: PdfViewerAnnotationSelection | null;
  setFallbackPageItems: (value: PdfFallbackPageItemsByPage) => void;
  viewerRef: React.RefObject<SearchablePdfViewerHandle | null>;
}

const AnalyzedContractViewer: React.FC<AnalyzedContractViewerProps> = ({
  currentPdfPage,
  onLoadingStateChange,
  selectedAnnotationReference,
  setFallbackPageItems,
  viewerRef
}) => {
  const { auth, token, user, userM, isDeveloper } = useContext(UserContext);
  const { currentDoc } = useContext(DocumentContext);
  const { policies } = usePolicyContext();
  const { setAlert } = useAlert();
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const notePanelRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const activeHighlightAllSelectionRequestRef =
    useRef<PdfViewerAnnotationRequest | null>(null);
  const highlightSelectionActionInFlightRef = useRef(false);
  const lastProcessedAnnotationSelectionKeyRef = useRef<string | null>(null);
  const highlightAllPreparationGenerationRef = useRef(0);
  const highlightAllPagePreparationPromisesRef = useRef<
    Map<
      number,
      {
        generation: number;
        promise: Promise<void>;
        signature: string;
      }
    >
  >(new Map());
  const highlightAllAppliedPageSignaturesRef = useRef<Record<number, string>>(
    {}
  );
  const highlightAllPreparedPageSignaturesRef = useRef<Record<number, string>>(
    {}
  );
  const highlightAllPageChangeVersionRef = useRef(0);
  const lastDisplayCardsKeyRef = useRef<string | null>(null);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [evaluations, setEvaluations] = useState<CandidateEvaluation[]>([]);
  const [notesByTargetId, setNotesByTargetId] = useState<
    Record<string, CandidateNote[]>
  >({});
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [openNoteCardKey, setOpenNoteCardKey] = useState<string | null>(null);
  const [postingNoteKey, setPostingNoteKey] = useState<string | null>(null);
  const [openActionsNoteId, setOpenActionsNoteId] = useState<string | null>(
    null
  );
  const [openActionsAnchorRect, setOpenActionsAnchorRect] =
    useState<DOMRect | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState<string>('');
  const [updatingNoteId, setUpdatingNoteId] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [isHeaderActionsMenuOpen, setIsHeaderActionsMenuOpen] =
    useState<boolean>(false);
  const [analyzedContractJson, setAnalyzedContractJson] =
    useState<unknown>(null);
  const [originalAnalyzedContractJson, setOriginalAnalyzedContractJson] =
    useState<unknown>(null);
  const [benchmarkRuleIdBySignalId, setBenchmarkRuleIdBySignalId] = useState<
    Record<string, string>
  >({});
  const [contextPageMap, setContextPageNumberById] = useState<
    Map<string, number[]>
  >(() => new Map());
  const [activeFilter, setActiveFilter] = useState<AnalysisFilter | null>(null);
  const [activeOutOfScopeImpact, setActiveOutOfScopeImpact] =
    useState<AnalyzedContractImpact | null>(null);
  const [activeDimensionGroupKey, setActiveDimensionGroupKey] = useState<
    string | null
  >(null);
  const [isSearchInputOpen, setIsSearchInputOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeCardKey, setActiveCardKey] = useState<string | null>(null);
  const [
    activeHighlightAllSelectionCardKey,
    setActiveHighlightAllSelectionCardKey
  ] = useState<string | null>(null);
  const [summaryInclusionByCardKey, setSummaryInclusionByCardKey] = useState<
    Record<string, boolean>
  >({});
  const [
    persistedSummaryInclusionByCardKey,
    setPersistedSummaryInclusionByCardKey
  ] = useState<Record<string, boolean>>({});
  const [ignoredSignalsByCardKey, setIgnoredSignalsByCardKey] = useState<
    Record<string, IgnoredSignalMetadata>
  >({});
  const [
    persistedIgnoredSignalsByCardKey,
    setPersistedIgnoredSignalsByCardKey
  ] = useState<Record<string, IgnoredSignalMetadata>>({});
  const [savingIncludedItems, setSavingIncludedItems] =
    useState<boolean>(false);
  const [downloadingCompanionDocument, setDownloadingCompanionDocument] =
    useState<boolean>(false);
  const [outOfScopeItems, setOutOfScopeItems] = useState<OutOfScope[]>([]);
  const [showAllItemsActive, setShowAllItemsActive] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const headerActionsMenuRef = useRef<HTMLDivElement | null>(null);
  const scrollElementIntoContentView = useCallback(
    (
      element: HTMLElement | null,
      options: {
        behavior?: ScrollBehavior;
        block?: 'start' | 'center' | 'nearest';
        paddingBottom?: number;
        paddingTop?: number;
      } = {}
    ) => {
      const container = scrollContentRef.current;

      if (!container || !element) {
        return;
      }

      const {
        behavior = 'smooth',
        block = 'nearest',
        paddingBottom = 24,
        paddingTop = 24
      } = options;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const currentScrollTop = container.scrollTop;
      const relativeTop =
        elementRect.top - containerRect.top + currentScrollTop;
      const relativeBottom =
        elementRect.bottom - containerRect.top + currentScrollTop;
      const maxScrollTop = Math.max(
        container.scrollHeight - container.clientHeight,
        0
      );
      let nextScrollTop = currentScrollTop;

      if (block === 'center') {
        nextScrollTop =
          relativeTop - (container.clientHeight - elementRect.height) / 2;
      } else if (block === 'start') {
        nextScrollTop = relativeTop - paddingTop;
      } else {
        const visibleTop = currentScrollTop + paddingTop;
        const visibleBottom =
          currentScrollTop + container.clientHeight - paddingBottom;

        if (relativeTop < visibleTop) {
          nextScrollTop = relativeTop - paddingTop;
        } else if (relativeBottom > visibleBottom) {
          nextScrollTop =
            relativeBottom - container.clientHeight + paddingBottom;
        } else {
          return;
        }
      }

      container.scrollTo({
        top: Math.min(Math.max(nextScrollTop, 0), maxScrollTop),
        behavior
      });
    },
    []
  );
  const documentId = searchParams.get('documentId');
  const showingOutOfScopeItems = activeFilter === 'not_applicable';
  const showingNotesItems = activeFilter === NOTES_FILTER;
  const showingIncludedSummaryItems = activeFilter === INCLUDED_SUMMARY_FILTER;
  const showingIgnoredItems = activeFilter === IGNORED_SIGNALS_FILTER;
  const hasAnalysisContent =
    evaluations.length > 0 || outOfScopeItems.length > 0;
  const normalizedSearchQuery = useMemo(
    () => normalizeViewerSearchText(searchQuery),
    [searchQuery]
  );
  const hasActiveSearchQuery = normalizedSearchQuery.length > 0;
  const nonIgnoredEvaluations = useMemo(
    () =>
      evaluations.filter(
        (candidateEvaluation, index) =>
          !ignoredSignalsByCardKey[
            getCandidateCardKey(candidateEvaluation, index)
          ]
      ),
    [evaluations, ignoredSignalsByCardKey]
  );
  const nonIgnoredOutOfScopeEntries = useMemo(
    () =>
      outOfScopeItems.flatMap((item, index) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);

        return ignoredSignalsByCardKey[outOfScopeKey] ? [] : [{ item, index }];
      }),
    [ignoredSignalsByCardKey, outOfScopeItems]
  );

  useEffect(() => {
    if (!isHeaderActionsMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (headerActionsMenuRef.current?.contains(target)) {
        return;
      }

      setIsHeaderActionsMenuOpen(false);
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsHeaderActionsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isHeaderActionsMenuOpen]);

  const noteCount = useMemo(
    () =>
      nonIgnoredEvaluations.reduce((count, { candidateId }) => {
        return (
          count +
          getNotesForTarget({
            notesByTargetId,
            targetId: candidateId
          }).length
        );
      }, 0) +
      nonIgnoredOutOfScopeEntries.reduce((count, { item, index }) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);
        const outOfScopeId = getOutOfScopeTargetId(item, outOfScopeKey);

        return (
          count +
          getNotesForTarget({
            notesByTargetId,
            targetId: outOfScopeId,
            fallbackTargetIds: getOutOfScopeFallbackTargetIds(item, index)
          }).length
        );
      }, 0),
    [nonIgnoredEvaluations, nonIgnoredOutOfScopeEntries, notesByTargetId]
  );
  const includedSummaryCount = useMemo(
    () =>
      Object.entries(summaryInclusionByCardKey).reduce(
        (count, [cardKey, isIncluded]) =>
          isIncluded && !ignoredSignalsByCardKey[cardKey] ? count + 1 : count,
        0
      ),
    [ignoredSignalsByCardKey, summaryInclusionByCardKey]
  );
  const ignoredSignalsCount = useMemo(
    () => Object.keys(ignoredSignalsByCardKey).length,
    [ignoredSignalsByCardKey]
  );
  const secondaryAnalysisFilterOptions = useMemo<AnalysisFilter[]>(
    () =>
      ignoredSignalsCount > 0
        ? [NOTES_FILTER, INCLUDED_SUMMARY_FILTER, IGNORED_SIGNALS_FILTER]
        : [NOTES_FILTER, INCLUDED_SUMMARY_FILTER],
    [ignoredSignalsCount]
  );

  const comparisonResultCounts = useMemo(() => {
    const counts = BASE_RESULT_FILTER_OPTIONS.reduce(
      (accumulator, result) => {
        if (result === NOTES_FILTER) {
          accumulator[result] = noteCount;
          return accumulator;
        }

        if (result === INCLUDED_SUMMARY_FILTER) {
          accumulator[result] = includedSummaryCount;
          return accumulator;
        }

        accumulator[result] =
          result === 'not_applicable'
            ? nonIgnoredOutOfScopeEntries.length
            : nonIgnoredEvaluations.filter(
                ({ evaluation }) => evaluation.comparison_result === result
              ).length;
        return accumulator;
      },
      {} as Record<AnalysisFilter, number>
    );

    counts[IGNORED_SIGNALS_FILTER] = ignoredSignalsCount;
    return counts;
  }, [
    ignoredSignalsCount,
    includedSummaryCount,
    nonIgnoredEvaluations,
    nonIgnoredOutOfScopeEntries.length,
    noteCount
  ]);
  const hasUnsavedIncludedItemsChanges = useMemo(
    () =>
      !areBooleanRecordsEqual(
        summaryInclusionByCardKey,
        persistedSummaryInclusionByCardKey
      ),
    [persistedSummaryInclusionByCardKey, summaryInclusionByCardKey]
  );
  const hasUnsavedIgnoredSignalsChanges = useMemo(
    () =>
      !areIgnoredSignalRecordsEqual(
        ignoredSignalsByCardKey,
        persistedIgnoredSignalsByCardKey
      ),
    [ignoredSignalsByCardKey, persistedIgnoredSignalsByCardKey]
  );
  const hasUnsavedAnalysisChanges =
    hasUnsavedIncludedItemsChanges || hasUnsavedIgnoredSignalsChanges;

  const filteredEvaluations = useMemo(() => {
    if (!activeFilter) {
      return nonIgnoredEvaluations;
    }

    if (activeFilter === NOTES_FILTER) {
      return nonIgnoredEvaluations.filter(
        ({ candidateId }) =>
          getNotesForTarget({
            notesByTargetId,
            targetId: candidateId
          }).length > 0
      );
    }

    if (activeFilter === INCLUDED_SUMMARY_FILTER) {
      return nonIgnoredEvaluations.filter((candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        return !!summaryInclusionByCardKey[cardKey];
      });
    }

    if (activeFilter === IGNORED_SIGNALS_FILTER) {
      return evaluations.filter((candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        return !!ignoredSignalsByCardKey[cardKey];
      });
    }

    return nonIgnoredEvaluations.filter(
      ({ evaluation }) => evaluation.comparison_result === activeFilter
    );
  }, [
    activeFilter,
    evaluations,
    ignoredSignalsByCardKey,
    nonIgnoredEvaluations,
    notesByTargetId,
    summaryInclusionByCardKey
  ]);
  const searchSourceEvaluations = useMemo(
    () => (hasActiveSearchQuery ? evaluations : filteredEvaluations),
    [evaluations, filteredEvaluations, hasActiveSearchQuery]
  );

  useEffect(() => {
    onLoadingStateChange?.(loading);
  }, [loading, onLoadingStateChange]);

  useEffect(() => {
    if (
      !showingOutOfScopeItems &&
      !showingNotesItems &&
      !showingIncludedSummaryItems &&
      !showingIgnoredItems
    ) {
      setActiveOutOfScopeImpact(null);
    }
  }, [
    showingIgnoredItems,
    showingIncludedSummaryItems,
    showingNotesItems,
    showingOutOfScopeItems
  ]);

  const groupedEvaluations = useMemo(
    () => buildDimensionGroups(searchSourceEvaluations),
    [searchSourceEvaluations]
  );
  const outOfScopeItemsWithNotes = useMemo(
    () =>
      nonIgnoredOutOfScopeEntries.flatMap(({ item, index }) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);
        const outOfScopeId = getOutOfScopeTargetId(item, outOfScopeKey);
        const notes = getNotesForTarget({
          notesByTargetId,
          targetId: outOfScopeId,
          fallbackTargetIds: getOutOfScopeFallbackTargetIds(item, index)
        });

        return notes.length > 0 ? [{ item, index }] : [];
      }),
    [nonIgnoredOutOfScopeEntries, notesByTargetId]
  );
  const outOfScopeItemsIncludedInSummary = useMemo(
    () =>
      nonIgnoredOutOfScopeEntries.flatMap(({ item, index }) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);

        return summaryInclusionByCardKey[outOfScopeKey]
          ? [{ item, index }]
          : [];
      }),
    [nonIgnoredOutOfScopeEntries, summaryInclusionByCardKey]
  );
  const outOfScopeItemsIgnored = useMemo(
    () =>
      outOfScopeItems.flatMap((item, index) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);

        return ignoredSignalsByCardKey[outOfScopeKey] ? [{ item, index }] : [];
      }),
    [ignoredSignalsByCardKey, outOfScopeItems]
  );
  const filteredOutOfScopeItems = useMemo(
    () =>
      nonIgnoredOutOfScopeEntries.flatMap(({ item, index }) =>
        !activeOutOfScopeImpact || item.impact === activeOutOfScopeImpact
          ? [{ item, index }]
          : []
      ),
    [activeOutOfScopeImpact, nonIgnoredOutOfScopeEntries]
  );
  const filteredOutOfScopeItemsWithNotes = useMemo(
    () =>
      outOfScopeItemsWithNotes.filter(
        ({ item }) =>
          !activeOutOfScopeImpact || item.impact === activeOutOfScopeImpact
      ),
    [activeOutOfScopeImpact, outOfScopeItemsWithNotes]
  );
  const filteredOutOfScopeItemsIncludedInSummary = useMemo(
    () =>
      outOfScopeItemsIncludedInSummary.filter(
        ({ item }) =>
          !activeOutOfScopeImpact || item.impact === activeOutOfScopeImpact
      ),
    [activeOutOfScopeImpact, outOfScopeItemsIncludedInSummary]
  );
  const filteredOutOfScopeItemsIgnored = useMemo(
    () =>
      outOfScopeItemsIgnored.filter(
        ({ item }) =>
          !activeOutOfScopeImpact || item.impact === activeOutOfScopeImpact
      ),
    [activeOutOfScopeImpact, outOfScopeItemsIgnored]
  );
  const allOutOfScopeEntries = useMemo(
    () => outOfScopeItems.map((item, index) => ({ item, index })),
    [outOfScopeItems]
  );
  const filterScopedOutOfScopeEntries = useMemo(
    () =>
      showingOutOfScopeItems
        ? filteredOutOfScopeItems
        : showingNotesItems
          ? filteredOutOfScopeItemsWithNotes
          : showingIncludedSummaryItems
            ? filteredOutOfScopeItemsIncludedInSummary
            : showingIgnoredItems
              ? filteredOutOfScopeItemsIgnored
              : [],
    [
      filteredOutOfScopeItemsIgnored,
      filteredOutOfScopeItems,
      filteredOutOfScopeItemsIncludedInSummary,
      filteredOutOfScopeItemsWithNotes,
      showingIgnoredItems,
      showingIncludedSummaryItems,
      showingNotesItems,
      showingOutOfScopeItems
    ]
  );
  const topLevelFilteredDimensionGroups = useMemo(
    () => buildDimensionGroups(filteredEvaluations),
    [filteredEvaluations]
  );
  const topLevelFilteredOutOfScopeDimensionGroups = useMemo(
    () => buildOutOfScopeDimensionGroups(filterScopedOutOfScopeEntries),
    [filterScopedOutOfScopeEntries]
  );
  const dimensionFilterOptions = useMemo(
    () =>
      [
        ...topLevelFilteredDimensionGroups.map(
          ({ benchmarkDimension, evaluatedDimension, items }) => {
            const groupKey = `${benchmarkDimension}|||${evaluatedDimension}`;

            return {
              label: `${formatDimensionGroupDisplayLabel(
                benchmarkDimension,
                evaluatedDimension
              )} (${items.length})`,
              value: groupKey
            };
          }
        ),
        ...topLevelFilteredOutOfScopeDimensionGroups.map(
          ({ groupKey, normalizedDimension, items }) => ({
            label: `${formatDimensionDisplayLabel(normalizedDimension)} (${items.length})`,
            value: groupKey
          })
        )
      ].sort((leftOption, rightOption) =>
        leftOption.label.localeCompare(rightOption.label)
      ),
    [topLevelFilteredDimensionGroups, topLevelFilteredOutOfScopeDimensionGroups]
  );
  const selectedDimensionFilterOption =
    dimensionFilterOptions.find(
      (option) => option.value === activeDimensionGroupKey
    ) ?? null;
  const activeDimensionFilterLabel =
    selectedDimensionFilterOption?.label.replace(/\s+\(\d+\)$/, '');
  const dimensionFilteredGroupedEvaluations = useMemo(() => {
    if (hasActiveSearchQuery || !activeDimensionGroupKey) {
      return groupedEvaluations;
    }

    return groupedEvaluations.filter(
      ({ benchmarkDimension, evaluatedDimension }) =>
        `${benchmarkDimension}|||${evaluatedDimension}` ===
        activeDimensionGroupKey
    );
  }, [activeDimensionGroupKey, groupedEvaluations, hasActiveSearchQuery]);
  const dimensionFilteredOutOfScopeEntries = useMemo(() => {
    if (hasActiveSearchQuery || !activeDimensionGroupKey) {
      return filterScopedOutOfScopeEntries;
    }

    return filterScopedOutOfScopeEntries.filter(
      ({ item }) =>
        getOutOfScopeDimensionGroupKey(item) === activeDimensionGroupKey
    );
  }, [
    activeDimensionGroupKey,
    filterScopedOutOfScopeEntries,
    hasActiveSearchQuery
  ]);
  useEffect(() => {
    if (!activeDimensionGroupKey) {
      return;
    }

    const hasMatchingDimensionOption = dimensionFilterOptions.some(
      (option) => option.value === activeDimensionGroupKey
    );

    if (!hasMatchingDimensionOption) {
      setActiveDimensionGroupKey(null);
    }
  }, [activeDimensionGroupKey, dimensionFilterOptions]);
  const visibleGroupedEvaluations = useMemo(() => {
    if (!normalizedSearchQuery) {
      return dimensionFilteredGroupedEvaluations;
    }

    return dimensionFilteredGroupedEvaluations
      .map((group) => ({
        ...group,
        items: group.items.filter((candidateEvaluation) =>
          matchesCandidateViewerSearch(
            candidateEvaluation,
            normalizedSearchQuery
          )
        )
      }))
      .filter((group) => group.items.length > 0);
  }, [dimensionFilteredGroupedEvaluations, normalizedSearchQuery]);

  const visibleEvaluationsCount = useMemo(
    () =>
      visibleGroupedEvaluations.reduce(
        (count, group) => count + group.items.length,
        0
      ),
    [visibleGroupedEvaluations]
  );
  const searchSourceOutOfScopeEntries = useMemo(
    () =>
      hasActiveSearchQuery
        ? allOutOfScopeEntries
        : dimensionFilteredOutOfScopeEntries,
    [
      allOutOfScopeEntries,
      dimensionFilteredOutOfScopeEntries,
      hasActiveSearchQuery
    ]
  );
  const visibleOutOfScopeEntries = useMemo(
    () =>
      !normalizedSearchQuery
        ? searchSourceOutOfScopeEntries
        : searchSourceOutOfScopeEntries.filter(({ item }) =>
            matchesOutOfScopeViewerSearch(item, normalizedSearchQuery)
          ),
    [normalizedSearchQuery, searchSourceOutOfScopeEntries]
  );
  const shouldRenderCandidateGroups =
    hasActiveSearchQuery || !showingOutOfScopeItems;
  const highlightAllAnnotationRequests = useMemo(() => {
    const requests = visibleOutOfScopeEntries.reduce<
      PdfViewerAnnotationRequest[]
    >((accumulator, { item, index }) => {
      const query = getOutOfScopeSourceText(item).trim();
      const contextIds = getOutOfScopeContextIds(item);

      if (!query || contextIds.length === 0) {
        return accumulator;
      }

      const pageNumbers = getResolvedPageNumbers({
        contextIds,
        contextPageMap
      });

      if (pageNumbers.length === 0) {
        return accumulator;
      }

      accumulator.push({
        pageNumber: pageNumbers[0],
        pageNumbers,
        query,
        reference: buildOutOfScopeAnnotationReference(item, index, pageNumbers)
      });

      return accumulator;
    }, []);

    if (shouldRenderCandidateGroups) {
      visibleGroupedEvaluations.forEach((group) => {
        group.items.forEach((candidateEvaluation) => {
          const query =
            candidateEvaluation.evaluation.evidence?.evaluated_source_text.trim();

          if (!query) {
            return;
          }

          const pageNumbers = getResolvedPageNumbers({
            contextIds: candidateEvaluation.contextIds,
            contextPageMap
          });

          if (pageNumbers.length === 0) {
            return;
          }

          requests.push({
            pageNumber: pageNumbers[0],
            pageNumbers,
            query,
            reference: buildCandidateAnnotationReference(
              candidateEvaluation,
              candidateEvaluation.index,
              pageNumbers
            )
          });
        });
      });
    }

    return requests;
  }, [
    contextPageMap,
    shouldRenderCandidateGroups,
    visibleGroupedEvaluations,
    visibleOutOfScopeEntries
  ]);
  const highlightAllBatchSignature = useMemo(
    () => buildHighlightAllBatchSignature(highlightAllAnnotationRequests),
    [highlightAllAnnotationRequests]
  );
  const highlightAllAnnotationRequestsByPage = useMemo(() => {
    const nextMap = new Map<number, PdfViewerAnnotationRequest[]>();

    highlightAllAnnotationRequests.forEach((request) => {
      const requestPageNumbers =
        request.pageNumbers?.length && request.pageNumbers.length > 0
          ? request.pageNumbers
          : request.pageNumber
            ? [request.pageNumber]
            : [];

      requestPageNumbers.forEach((pageNumber) => {
        const currentRequests = nextMap.get(pageNumber) ?? [];

        currentRequests.push(request);
        nextMap.set(pageNumber, currentRequests);
      });
    });

    return nextMap;
  }, [highlightAllAnnotationRequests]);
  const highlightAllBatchSignatureByPage = useMemo(
    () =>
      new Map(
        Array.from(highlightAllAnnotationRequestsByPage.entries()).map(
          ([pageNumber, requests]) => [
            pageNumber,
            buildHighlightAllBatchSignature(requests)
          ]
        )
      ),
    [highlightAllAnnotationRequestsByPage]
  );
  const getHighlightAllRequestsForPage = useCallback(
    (pageNumber: number) =>
      highlightAllAnnotationRequestsByPage.get(pageNumber) ?? [],
    [highlightAllAnnotationRequestsByPage]
  );
  const getHighlightAllBatchSignatureForPage = useCallback(
    (pageNumber: number) =>
      highlightAllBatchSignatureByPage.get(pageNumber) ??
      `${highlightAllBatchSignature}::page-${pageNumber}`,
    [highlightAllBatchSignature, highlightAllBatchSignatureByPage]
  );
  const visibleItemCount = hasActiveSearchQuery
    ? visibleEvaluationsCount + visibleOutOfScopeEntries.length
    : showingOutOfScopeItems
      ? visibleOutOfScopeEntries.length
      : showingNotesItems
        ? visibleEvaluationsCount + visibleOutOfScopeEntries.length
        : showingIncludedSummaryItems
          ? visibleEvaluationsCount + visibleOutOfScopeEntries.length
          : showingIgnoredItems
            ? visibleEvaluationsCount + visibleOutOfScopeEntries.length
            : visibleEvaluationsCount;
  const displayCardsKey = hasActiveSearchQuery
    ? ['search-all-cards', normalizedSearchQuery || 'all'].join('::')
    : [
        activeFilter ?? 'all',
        activeOutOfScopeImpact ?? 'all',
        activeDimensionGroupKey ?? 'all',
        normalizedSearchQuery || 'all'
      ].join('::');
  const clearActiveCardSelection = useCallback(() => {
    activeHighlightAllSelectionRequestRef.current = null;
    setActiveCardKey(null);
    setActiveHighlightAllSelectionCardKey(null);
    setOpenNoteCardKey(null);
  }, []);
  const resetHighlightSelectionState = useCallback(() => {
    clearActiveCardSelection();
    highlightAllPageChangeVersionRef.current += 1;
    setShowAllItemsActive(false);
    highlightAllAppliedPageSignaturesRef.current = {};
  }, [clearActiveCardSelection]);
  const clearViewerAnnotations = useCallback(
    async (showError = true) => {
      if (!viewerRef.current?.clearAnnotations) {
        if (showError) {
          setAlert(
            'PDF annotations are not ready to be cleared yet.',
            'error',
            4
          );
        }

        return;
      }

      try {
        await viewerRef.current.clearAnnotations('programmatic');
        highlightAllAppliedPageSignaturesRef.current = {};
      } catch (clearAnnotationsError) {
        if (showError) {
          setAlert(
            getErrorMessage(
              clearAnnotationsError,
              'Unable to clear PDF annotations.'
            ),
            'error',
            4
          );
        }
      }
    },
    [setAlert, viewerRef]
  );
  const buildPageScopedAnnotationRequest = useCallback(
    (
      request: PdfViewerAnnotationRequest,
      pageNumber: number
    ): PdfViewerAnnotationRequest => ({
      ...request,
      pageNumber,
      pageNumbers: [pageNumber],
      reference: request.reference
        ? {
            ...request.reference,
            pageNumbers: [pageNumber]
          }
        : request.reference,
      scrollToPage: false
    }),
    []
  );
  const removeMatchingAnnotationRequest = useCallback(
    async (
      request: PdfViewerAnnotationRequest,
      annotationStyle: PdfViewerAnnotationStyle = 'default',
      pageNumber?: number
    ) => {
      const removeMatchingAnnotations =
        viewerRef.current?.removeMatchingAnnotations;

      if (!removeMatchingAnnotations) {
        throw new Error('PDF annotations are not ready yet.');
      }

      const scopedRequest =
        typeof pageNumber === 'number'
          ? buildPageScopedAnnotationRequest(request, pageNumber)
          : {
              ...request,
              scrollToPage: false
            };

      await removeMatchingAnnotations({
        ...scopedRequest,
        annotationStyle,
        scrollToPage: false
      });
    },
    [buildPageScopedAnnotationRequest, viewerRef]
  );
  const reportHighlightAllError = useCallback(
    (error: unknown) => {
      if (isSiblingTaskAbortError(error)) {
        console.warn(
          '[PDF annotations] ignoring aborted highlight-all task',
          error
        );
        return;
      }

      setAlert(
        getErrorMessage(error, 'Unable to highlight visible items.'),
        'error',
        4
      );
    },
    [setAlert]
  );
  const ensureHighlightAllPagePrepared = useCallback(
    async (pageNumber: number) => {
      const prepareAnnotationBatch = viewerRef.current?.prepareAnnotationBatch;
      const pageRequests = getHighlightAllRequestsForPage(pageNumber);
      const pageSignature = getHighlightAllBatchSignatureForPage(pageNumber);
      const preparationGeneration =
        highlightAllPreparationGenerationRef.current;

      if (!prepareAnnotationBatch) {
        throw new Error('PDF annotations are not ready yet.');
      }

      if (pageRequests.length === 0) {
        return;
      }

      if (
        highlightAllPreparedPageSignaturesRef.current[pageNumber] ===
        pageSignature
      ) {
        return;
      }

      const inFlightPreparation =
        highlightAllPagePreparationPromisesRef.current.get(pageNumber);

      if (
        inFlightPreparation &&
        inFlightPreparation.generation === preparationGeneration &&
        inFlightPreparation.signature === pageSignature
      ) {
        await inFlightPreparation.promise;
        return;
      }

      const nextPreparationPromise = (async () => {
        await prepareAnnotationBatch(
          getHighlightAllPageCacheKey(pageNumber),
          pageRequests
        );

        if (
          highlightAllPreparationGenerationRef.current !== preparationGeneration
        ) {
          return;
        }

        highlightAllPreparedPageSignaturesRef.current = {
          ...highlightAllPreparedPageSignaturesRef.current,
          [pageNumber]: pageSignature
        };
      })();

      highlightAllPagePreparationPromisesRef.current.set(pageNumber, {
        generation: preparationGeneration,
        promise: nextPreparationPromise,
        signature: pageSignature
      });

      try {
        await nextPreparationPromise;
      } finally {
        const currentPreparation =
          highlightAllPagePreparationPromisesRef.current.get(pageNumber);

        if (currentPreparation?.promise === nextPreparationPromise) {
          highlightAllPagePreparationPromisesRef.current.delete(pageNumber);
        }
      }
    },
    [
      getHighlightAllBatchSignatureForPage,
      getHighlightAllRequestsForPage,
      viewerRef
    ]
  );
  const showHighlightAllPage = useCallback(
    async (pageNumber: number) => {
      const applyPreparedAnnotationBatch =
        viewerRef.current?.applyPreparedAnnotationBatch;
      const pageRequests = getHighlightAllRequestsForPage(pageNumber);
      const pageSignature = getHighlightAllBatchSignatureForPage(pageNumber);

      if (!applyPreparedAnnotationBatch) {
        throw new Error('PDF annotations are not ready yet.');
      }

      if (
        highlightAllAppliedPageSignaturesRef.current[pageNumber] ===
        pageSignature
      ) {
        return;
      }

      if (pageRequests.length === 0) {
        return;
      }

      const nextVersion = highlightAllPageChangeVersionRef.current + 1;

      highlightAllPageChangeVersionRef.current = nextVersion;

      await ensureHighlightAllPagePrepared(pageNumber);

      if (highlightAllPageChangeVersionRef.current !== nextVersion) {
        return;
      }

      await applyPreparedAnnotationBatch(
        getHighlightAllPageCacheKey(pageNumber),
        {
          pageNumber,
          scrollToPage: false
        }
      );

      if (highlightAllPageChangeVersionRef.current === nextVersion) {
        highlightAllAppliedPageSignaturesRef.current = {
          ...highlightAllAppliedPageSignaturesRef.current,
          [pageNumber]: pageSignature
        };
      }
    },
    [
      ensureHighlightAllPagePrepared,
      getHighlightAllBatchSignatureForPage,
      getHighlightAllRequestsForPage,
      viewerRef
    ]
  );
  const setActiveEntryFromReference = (
    annotationReference: PdfViewerAnnotationReference,
    options: {
      activateCard?: boolean;
    } = {}
  ) => {
    const { activateCard = true } = options;
    const normalizedCandidateId = annotationReference.candidateId?.trim();
    const referenceContextIds = toContextIdArray(
      annotationReference.contextIds,
      annotationReference.contextId
    );
    const normalizedSourceText = normalizeComparableText(
      annotationReference.sourceText
    );
    let matchedCardKey: string | null = null;

    const matchedOutOfScopeEntry = visibleOutOfScopeEntries.find(
      ({ item, index }) => {
        const cardKey = getOutOfScopeCardKey(item, index);

        if (cardKey === annotationReference.candidateCardKey) {
          return true;
        }

        return Boolean(
          normalizedSourceText &&
            (referenceContextIds.length === 0 ||
              hasMatchingContextId(
                getOutOfScopeContextIds(item),
                referenceContextIds
              )) &&
            normalizeComparableText(getOutOfScopeSourceText(item)) ===
              normalizedSourceText
        );
      }
    );

    if (matchedOutOfScopeEntry) {
      matchedCardKey = getOutOfScopeCardKey(
        matchedOutOfScopeEntry.item,
        matchedOutOfScopeEntry.index
      );
    }

    if (!matchedCardKey && !showingOutOfScopeItems) {
      for (const group of visibleGroupedEvaluations) {
        const matchedCandidate = group.items.find((candidateEvaluation) => {
          const cardKey = getCandidateCardKey(
            candidateEvaluation,
            candidateEvaluation.index
          );

          if (cardKey === annotationReference.candidateCardKey) {
            return true;
          }

          if (
            normalizedCandidateId &&
            candidateEvaluation.candidateId.trim() === normalizedCandidateId
          ) {
            return true;
          }

          return Boolean(
            normalizedSourceText &&
              normalizeComparableText(
                candidateEvaluation.evaluation.evidence?.evaluated_source_text
              ) === normalizedSourceText &&
              (referenceContextIds.length === 0 ||
                hasMatchingContextId(
                  candidateEvaluation.contextIds,
                  referenceContextIds
                ))
          );
        });

        if (matchedCandidate) {
          matchedCardKey = getCandidateCardKey(
            matchedCandidate,
            matchedCandidate.index
          );
          break;
        }
      }
    }

    if (!matchedCardKey) {
      return false;
    }

    if (activateCard) {
      setActiveCardKey(matchedCardKey);
    }

    window.requestAnimationFrame(() => {
      scrollElementIntoContentView(cardRefs.current[matchedCardKey!] ?? null, {
        behavior: 'smooth',
        block: 'center'
      });
    });

    return true;
  };
  const toggleShowAllItems = async () => {
    if (showAllItemsActive) {
      resetHighlightSelectionState();
      await clearViewerAnnotations(false);
      return;
    }

    if (
      !viewerRef.current?.prepareAnnotationBatch ||
      !viewerRef.current?.applyPreparedAnnotationBatch
    ) {
      setAlert('PDF annotations are not ready yet.', 'error', 4);
      return;
    }

    if (highlightAllAnnotationRequests.length === 0) {
      setAlert('No source text was found for the visible items.', 'error', 4);
      return;
    }

    clearActiveCardSelection();

    try {
      await clearViewerAnnotations(false);
      setShowAllItemsActive(true);
      setAlert(
        'Highlight all mode enabled. Pages will highlight as you scroll.',
        'success',
        4
      );
    } catch (highlightAllError) {
      setAlert(
        getErrorMessage(highlightAllError, 'Unable to highlight all items.'),
        'error',
        4
      );
    }
  };
  const highlightSingleItem = async (
    cardKey: string,
    request: PdfViewerAnnotationRequest,
    isActive: boolean
  ) => {
    const activeHighlightAllSelectionRequest =
      activeHighlightAllSelectionRequestRef.current;
    const isCurrentHighlightAllSelection =
      activeHighlightAllSelectionCardKey === cardKey;

    if (isActive && !showAllItemsActive) {
      await clearViewerAnnotations(false);
      resetHighlightSelectionState();
      return;
    }

    if (
      !viewerRef.current?.annotateMatches ||
      (showAllItemsActive && !viewerRef.current?.removeMatchingAnnotations)
    ) {
      setAlert('PDF annotations are not ready yet.', 'error', 4);
      return;
    }

    if (highlightSelectionActionInFlightRef.current) {
      return;
    }

    highlightSelectionActionInFlightRef.current = true;

    try {
      if (showAllItemsActive) {
        console.log(
          'in highlightSingleItem',
          isCurrentHighlightAllSelection,
          activeHighlightAllSelectionRequest
        );
        if (
          isCurrentHighlightAllSelection &&
          activeHighlightAllSelectionRequest
        ) {
          await removeMatchingAnnotationRequest(
            activeHighlightAllSelectionRequest,
            'active-selection'
          );
          clearActiveCardSelection();
          return;
        }

        if (isCurrentHighlightAllSelection) {
          clearActiveCardSelection();
          return;
        }

        if (activeHighlightAllSelectionRequest) {
          await removeMatchingAnnotationRequest(
            activeHighlightAllSelectionRequest,
            'active-selection'
          );
          activeHighlightAllSelectionRequestRef.current = null;
          setActiveHighlightAllSelectionCardKey(null);
        }

        await viewerRef.current.annotateMatches({
          ...request,
          annotationStyle: 'active-selection'
        });
        activeHighlightAllSelectionRequestRef.current = {
          ...request,
          pageNumbers: request.pageNumbers
            ? [...request.pageNumbers]
            : undefined,
          query: Array.isArray(request.query)
            ? [...request.query]
            : request.query,
          reference: request.reference
            ? {
                ...request.reference,
                contextIds: request.reference.contextIds
                  ? [...request.reference.contextIds]
                  : undefined,
                pageNumbers: request.reference.pageNumbers
                  ? [...request.reference.pageNumbers]
                  : undefined,
                sentenceIds: request.reference.sentenceIds
                  ? [...request.reference.sentenceIds]
                  : undefined
              }
            : undefined
        };
        setActiveHighlightAllSelectionCardKey(cardKey);
        setActiveCardKey(null);
        return;
      }

      resetHighlightSelectionState();
      setActiveCardKey(cardKey);
      await clearViewerAnnotations(false);
      await viewerRef.current.annotateMatches({
        ...request
      });
    } catch (highlightError) {
      if (!showAllItemsActive) {
        setActiveCardKey(null);
      }
      setAlert(
        getErrorMessage(
          highlightError,
          'Unable to highlight the selected item.'
        ),
        'error',
        4
      );
    } finally {
      highlightSelectionActionInFlightRef.current = false;
    }
  };
  useEffect(() => {
    if (!selectedAnnotationReference) {
      return;
    }

    if (
      selectedAnnotationReference.selectionKey ===
      lastProcessedAnnotationSelectionKeyRef.current
    ) {
      return;
    }

    if (highlightSelectionActionInFlightRef.current) {
      lastProcessedAnnotationSelectionKeyRef.current =
        selectedAnnotationReference.selectionKey;
      return;
    }

    if (showAllItemsActive) {
      console.log('in useEffect line 3557');
      setActiveEntryFromReference(selectedAnnotationReference.reference, {
        activateCard: false
      });
      lastProcessedAnnotationSelectionKeyRef.current =
        selectedAnnotationReference.selectionKey;
      return;
    }

    const matched = setActiveEntryFromReference(
      selectedAnnotationReference.reference
    );

    if (!matched) {
      return;
    }

    lastProcessedAnnotationSelectionKeyRef.current =
      selectedAnnotationReference.selectionKey;
    // The helper closes over the visible card collections already listed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAnnotationReference,
    showAllItemsActive,
    showingOutOfScopeItems,
    visibleGroupedEvaluations,
    visibleOutOfScopeEntries
  ]);
  useEffect(() => {
    highlightAllPreparationGenerationRef.current += 1;
    highlightAllPagePreparationPromisesRef.current.clear();
    highlightAllAppliedPageSignaturesRef.current = {};
    highlightAllPreparedPageSignaturesRef.current = {};
    highlightAllPageChangeVersionRef.current += 1;
  }, [highlightAllBatchSignature]);
  useEffect(() => {
    if (!showAllItemsActive) {
      return;
    }

    const availablePageNumbers = Array.from(
      highlightAllAnnotationRequestsByPage.keys()
    ).sort(
      (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
    );
    const targetPageNumber =
      currentPdfPage === null || currentPdfPage === undefined
        ? (availablePageNumbers[0] ?? null)
        : highlightAllAnnotationRequestsByPage.has(currentPdfPage)
          ? currentPdfPage
          : null;

    if (!targetPageNumber) {
      return;
    }

    void showHighlightAllPage(targetPageNumber).catch((highlightAllError) => {
      reportHighlightAllError(highlightAllError);
    });
  }, [
    currentPdfPage,
    highlightAllAnnotationRequestsByPage,
    reportHighlightAllError,
    showAllItemsActive,
    showHighlightAllPage
  ]);
  useEffect(() => {
    if (!showAllItemsActive) {
      return;
    }

    const availablePageNumbers = Array.from(
      highlightAllAnnotationRequestsByPage.keys()
    ).sort(
      (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
    );
    const targetPageNumber =
      currentPdfPage === null || currentPdfPage === undefined
        ? (availablePageNumbers[0] ?? null)
        : highlightAllAnnotationRequestsByPage.has(currentPdfPage)
          ? currentPdfPage
          : null;

    if (!targetPageNumber) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const targetSignature =
        getHighlightAllBatchSignatureForPage(targetPageNumber);

      if (
        highlightAllAppliedPageSignaturesRef.current[targetPageNumber] ===
        targetSignature
      ) {
        return;
      }

      void showHighlightAllPage(targetPageNumber).catch((highlightAllError) => {
        reportHighlightAllError(highlightAllError);
      });
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [
    currentPdfPage,
    getHighlightAllBatchSignatureForPage,
    highlightAllAnnotationRequestsByPage,
    reportHighlightAllError,
    showAllItemsActive,
    showHighlightAllPage
  ]);
  const handleAnalysisFilterChange = (result: AnalysisFilter) => {
    const nextFilter = activeFilter === result ? null : result;
    const keepsOutOfScopeImpactFilter =
      nextFilter === 'not_applicable' ||
      nextFilter === NOTES_FILTER ||
      nextFilter === INCLUDED_SUMMARY_FILTER ||
      nextFilter === IGNORED_SIGNALS_FILTER;

    setActiveFilter(nextFilter);
    setActiveDimensionGroupKey(null);

    if (!keepsOutOfScopeImpactFilter) {
      setActiveOutOfScopeImpact(null);
    }

    if (nextFilter !== activeFilter && !hasActiveSearchQuery) {
      clearActiveCardSelection();

      if (!showAllItemsActive) {
        void clearViewerAnnotations(false);
      }
    }
  };
  const toggleSearchInput = useCallback(() => {
    if (isSearchInputOpen) {
      setIsSearchInputOpen(false);
      setSearchQuery('');
      return;
    }

    setIsSearchInputOpen(true);
  }, [isSearchInputOpen]);
  useEffect(() => {
    if (activeFilter === IGNORED_SIGNALS_FILTER && ignoredSignalsCount === 0) {
      setActiveFilter(null);
    }
  }, [activeFilter, ignoredSignalsCount]);
  useEffect(() => {
    if (!isSearchInputOpen) {
      return;
    }

    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [isSearchInputOpen]);
  useEffect(() => {
    if (lastDisplayCardsKeyRef.current === null) {
      lastDisplayCardsKeyRef.current = displayCardsKey;
      return;
    }

    if (lastDisplayCardsKeyRef.current === displayCardsKey) {
      return;
    }

    lastDisplayCardsKeyRef.current = displayCardsKey;

    if (!showAllItemsActive) {
      return;
    }

    clearActiveCardSelection();

    const availablePageNumbers = Array.from(
      highlightAllAnnotationRequestsByPage.keys()
    ).sort(
      (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
    );
    const targetPageNumber =
      currentPdfPage === null || currentPdfPage === undefined
        ? (availablePageNumbers[0] ?? null)
        : highlightAllAnnotationRequestsByPage.has(currentPdfPage)
          ? currentPdfPage
          : null;

    void (async () => {
      await clearViewerAnnotations(false);

      if (!targetPageNumber) {
        return;
      }

      await showHighlightAllPage(targetPageNumber);
    })().catch((highlightAllError) => {
      reportHighlightAllError(highlightAllError);
    });
  }, [
    clearActiveCardSelection,
    clearViewerAnnotations,
    currentPdfPage,
    displayCardsKey,
    highlightAllAnnotationRequestsByPage,
    reportHighlightAllError,
    showAllItemsActive,
    showHighlightAllPage
  ]);

  const noteOwner = user?.name?.trim() || user?.email?.trim() || 'Unknown user';
  const currentUserId =
    normalizeNoteTargetId(
      (userM as unknown as { _id?: string })?._id || userM?.sub || user?.sub
    ) || '';
  useUnsavedChangesWarning(
    hasUnsavedAnalysisChanges,
    'You have unsaved analyzed contract changes. Are you sure you want to leave this page?'
  );
  const companionDocumentBaseName = useMemo(
    () =>
      getCompanionDocumentBaseName(
        currentDoc?._id === documentId ? currentDoc.documentFileName : '',
        documentId ? `analyzed-contract-${documentId}` : 'analyzed-contract'
      ),
    [currentDoc?._id, currentDoc?.documentFileName, documentId]
  );

  const companionDocumentSections = useMemo(() => {
    const fallbackOrder = {
      pageNumber: 'Unspecified' as const,
      sortMinY: Number.POSITIVE_INFINITY,
      sortPage: Number.MAX_SAFE_INTEGER,
      sortSentenceIndex: Number.MAX_SAFE_INTEGER
    };

    const getDocumentOrder = (contextIds: Array<string | null | undefined>) => {
      const pageNumber = getPageNumberForContextIds(contextIds, contextPageMap);

      if (pageNumber !== undefined) {
        return {
          pageNumber,
          sortMinY: Number.POSITIVE_INFINITY,
          sortPage: pageNumber,
          sortSentenceIndex: Number.MAX_SAFE_INTEGER
        };
      }

      return fallbackOrder;
    };

    const candidateEntries = evaluations.flatMap(
      ({ candidateId, contextIds, evaluation, cardKey }, index) => {
        const resolvedCardKey = cardKey || `${candidateId}-${index}`;

        if (!summaryInclusionByCardKey[resolvedCardKey]) {
          return [];
        }

        const notes = getNotesForTarget({
          notesByTargetId,
          targetId: candidateId
        });
        const documentOrder = getDocumentOrder(contextIds);

        return [
          {
            comparisonColor: evaluation.comparison_result
              ? formatComparisonResultColor(evaluation.comparison_result)
              : undefined,
            comparisonLabel: evaluation.comparison_result
              ? formatComparisonResultLabel(evaluation.comparison_result)
              : undefined,
            ...documentOrder,
            originalIndex: index,
            text: buildCandidateCompanionText({
              evaluation
            }),
            notes: buildCompanionNoteEntries(notes)
          }
        ] satisfies CompanionDocumentEntry[];
      }
    );

    const outOfScopeEntries = outOfScopeItems.flatMap((item, index) => {
      const outOfScopeKey = getOutOfScopeCardKey(item, index);

      if (!summaryInclusionByCardKey[outOfScopeKey]) {
        return [];
      }

      const outOfScopeId = getOutOfScopeTargetId(item, outOfScopeKey);
      const notes = getNotesForTarget({
        notesByTargetId,
        targetId: outOfScopeId,
        fallbackTargetIds: getOutOfScopeFallbackTargetIds(item, index)
      });
      const documentOrder = getDocumentOrder(getOutOfScopeContextIds(item));

      return [
        {
          comparisonColor: formatComparisonResultColor('not_applicable'),
          comparisonLabel: formatComparisonResultLabel('not_applicable'),
          ...documentOrder,
          originalIndex: evaluations.length + index,
          text: buildOutOfScopeCompanionText({
            item
          }),
          notes: buildCompanionNoteEntries(notes)
        }
      ] satisfies CompanionDocumentEntry[];
    });

    const sortedEntries = [...candidateEntries, ...outOfScopeEntries].sort(
      (leftEntry, rightEntry) => {
        if (leftEntry.sortPage !== rightEntry.sortPage) {
          return leftEntry.sortPage - rightEntry.sortPage;
        }

        if (leftEntry.sortMinY !== rightEntry.sortMinY) {
          return leftEntry.sortMinY - rightEntry.sortMinY;
        }

        if (leftEntry.sortSentenceIndex !== rightEntry.sortSentenceIndex) {
          return leftEntry.sortSentenceIndex - rightEntry.sortSentenceIndex;
        }

        return leftEntry.originalIndex - rightEntry.originalIndex;
      }
    );

    return sortedEntries.reduce((sections, entry) => {
      const lastSection = sections[sections.length - 1];

      if (lastSection && lastSection.pageNumber === entry.pageNumber) {
        lastSection.items.push({
          comparisonColor: entry.comparisonColor,
          comparisonLabel: entry.comparisonLabel,
          text: entry.text,
          notes: entry.notes
        });

        return sections;
      }

      sections.push({
        pageNumber: entry.pageNumber,
        items: [
          {
            comparisonColor: entry.comparisonColor,
            comparisonLabel: entry.comparisonLabel,
            text: entry.text,
            notes: entry.notes
          }
        ]
      });

      return sections;
    }, [] as ParagraphSection[]);
  }, [
    contextPageMap,
    evaluations,
    notesByTargetId,
    outOfScopeItems,
    summaryInclusionByCardKey
  ]);

  const setNoteDraft = (cardKey: string, value: string) => {
    setNoteDrafts((previousDrafts) => ({
      ...previousDrafts,
      [cardKey]: value
    }));
  };

  const setIncludeInSummary = (cardKey: string, value: boolean) => {
    setSummaryInclusionByCardKey((previousState) => ({
      ...previousState,
      [cardKey]: value
    }));
  };

  const setIgnoredSignal = (cardKey: string, value: boolean) => {
    setIgnoredSignalsByCardKey((previousState) => {
      if (!value) {
        const nextState = { ...previousState };

        delete nextState[cardKey];
        return nextState;
      }

      return {
        ...previousState,
        [cardKey]: {
          ignoredAt: new Date().toISOString(),
          ignoredByName: noteOwner,
          ignoredByUserId: currentUserId || undefined
        }
      };
    });
  };

  const saveAnalyzedContractChanges = async () => {
    if (!documentId) {
      setAlert(
        'Missing document id. Unable to save analyzed contract changes.',
        'error',
        4
      );
      return;
    }

    if (!analyzedContractJson) {
      setAlert(
        'The analyzed contract JSON is not loaded. Unable to save analyzed contract changes.',
        'error',
        4
      );
      return;
    }

    setSavingIncludedItems(true);

    try {
      const nextAnalyzedContractJson = cloneJsonValue(analyzedContractJson);
      let updatedRecordCount = 0;

      evaluations.forEach((candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        if (!candidateEvaluation.sourcePath?.length) {
          return;
        }

        const didUpdate = updateRecordAtPath(
          nextAnalyzedContractJson,
          candidateEvaluation.sourcePath,
          (record) => {
            record[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] =
              !!summaryInclusionByCardKey[cardKey];
            applyIgnoredSignalMetadataToRecord(
              record,
              ignoredSignalsByCardKey[cardKey]
            );
          }
        );

        if (didUpdate) {
          updatedRecordCount += 1;
        }
      });

      outOfScopeItems.forEach((item, index) => {
        const cardKey = getOutOfScopeCardKey(item, index);

        if (!item.sourcePath?.length) {
          return;
        }

        const didUpdate = updateRecordAtPath(
          nextAnalyzedContractJson,
          item.sourcePath,
          (record) => {
            record[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] =
              !!summaryInclusionByCardKey[cardKey];
            applyIgnoredSignalMetadataToRecord(
              record,
              ignoredSignalsByCardKey[cardKey]
            );
          }
        );

        if (didUpdate) {
          updatedRecordCount += 1;
        }
      });

      if (updatedRecordCount === 0) {
        throw new Error('No analyzed contract entries were available to save.');
      }

      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=saveAnalyzedContract`,
        { documentId },
        auth
      );
      const uploadUrl = presignedUrl?.data?.url;

      if (!uploadUrl || uploadUrl === 'locked') {
        throw new Error(
          'No upload URL was returned for the analyzed contract.'
        );
      }

      const blob = new Blob([JSON.stringify(nextAnalyzedContractJson)], {
        type: 'application/json'
      });
      await axios.put(uploadUrl, blob, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      setAnalyzedContractJson(nextAnalyzedContractJson);
      setOriginalAnalyzedContractJson(cloneJsonValue(nextAnalyzedContractJson));
      setPersistedSummaryInclusionByCardKey({
        ...summaryInclusionByCardKey
      });
      setPersistedIgnoredSignalsByCardKey({
        ...ignoredSignalsByCardKey
      });
      setAlert('Analyzed contract changes saved.', 'success', 3);
    } catch (saveError) {
      setAlert(
        getErrorMessage(saveError, 'Unable to save analyzed contract changes.'),
        'error',
        4
      );
    } finally {
      setSavingIncludedItems(false);
    }
  };

  const toggleNotePanel = (cardKey: string) => {
    setOpenNoteCardKey((currentValue) =>
      currentValue === cardKey ? null : cardKey
    );
  };

  const restoreScrollContentPosition = useCallback(
    (scrollTop: number | null) => {
      if (scrollTop === null) {
        return;
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!scrollContentRef.current) {
            return;
          }

          scrollContentRef.current.scrollTop = scrollTop;
        });
      });
    },
    []
  );

  const postCandidateNote = async ({
    cardKey,
    target
  }: {
    cardKey: string;
    target: AnalyzedContractNoteTarget;
  }) => {
    const noteText = noteDrafts[cardKey]?.trim();

    if (!noteText) {
      return;
    }

    if (!documentId) {
      setAlert('Missing document id. Unable to post note.', 'error', 4);
      return;
    }

    setPostingNoteKey(cardKey);
    const preservedScrollTop = scrollContentRef.current?.scrollTop ?? null;

    try {
      const createdNote = await createCandidateNote(
        {
          documentId,
          noteText,
          owner: noteOwner,
          target
        },
        auth
      );
      const noteForState = {
        ...createdNote,
        userId: createdNote.userId || currentUserId
      };
      const normalizedTarget = normalizeNoteTarget(target);
      const noteTargetId =
        normalizedTarget.type === 'candidate'
          ? getNoteTargetCandidateId(normalizedTarget)
          : getNoteTargetOutOfScopeId(normalizedTarget);

      setNotesByTargetId((previousNotes) => ({
        ...previousNotes,
        [noteTargetId]: [...(previousNotes[noteTargetId] || []), noteForState]
      }));
      setNoteDraft(cardKey, '');
      restoreScrollContentPosition(preservedScrollTop);
      setAlert('Note posted.', 'success', 3);
    } catch (noteError) {
      setAlert(getErrorMessage(noteError, 'Unable to post note.'), 'error', 4);
    } finally {
      setPostingNoteKey(null);
    }
  };

  const canMutateNote = (note: CandidateNote) =>
    !!currentUserId &&
    note.userId === currentUserId &&
    !!note.noteDocumentId &&
    !!note.textId &&
    Object.values(notesByTargetId).some(
      (notes) => notes[notes.length - 1]?.id === note.id
    );

  const startEditingNote = (note: CandidateNote) => {
    if (!canMutateNote(note)) {
      setAlert(
        'Only the latest note can be edited by its author.',
        'warning',
        4
      );
      return;
    }

    setEditingNoteId(note.id);
    setEditNoteValue(note.text);
    setOpenActionsNoteId(null);
    setOpenActionsAnchorRect(null);
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditNoteValue('');
  };

  const saveEditedNote = async (note: CandidateNote) => {
    const updatedText = editNoteValue.trim();

    if (!updatedText) {
      return;
    }

    if (!documentId) {
      setAlert('Missing document id. Unable to update note.', 'error', 4);
      return;
    }

    if (!canMutateNote(note)) {
      setAlert(
        'Only the latest note can be edited by its author.',
        'warning',
        4
      );
      return;
    }

    setUpdatingNoteId(note.id);

    try {
      await updateCandidateNoteText(
        {
          documentId,
          noteText_id: note.textId,
          noteId: note.noteDocumentId,
          text: updatedText
        },
        auth
      );

      setNotesByTargetId((previousNotes) =>
        Object.entries(previousNotes).reduce(
          (nextNotes, [targetId, notes]) => {
            nextNotes[targetId] = notes.map((currentNote) =>
              currentNote.id === note.id
                ? { ...currentNote, text: updatedText }
                : currentNote
            );
            return nextNotes;
          },
          {} as Record<string, CandidateNote[]>
        )
      );
      cancelEditingNote();
      setAlert('Note updated.', 'success', 3);
    } catch (noteError) {
      setAlert(
        getErrorMessage(noteError, 'Unable to update note.'),
        'error',
        4
      );
    } finally {
      setUpdatingNoteId(null);
    }
  };

  const deleteNote = async (note: CandidateNote) => {
    if (!documentId) {
      setAlert('Missing document id. Unable to delete note.', 'error', 4);
      return;
    }

    if (!canMutateNote(note)) {
      setAlert(
        'Only the latest note can be deleted by its author.',
        'warning',
        4
      );
      return;
    }

    setDeletingNoteId(note.id);

    try {
      await deleteCandidateNoteText(
        {
          documentId,
          _id: note.noteDocumentId,
          textId: note.textId
        },
        auth
      );

      setNotesByTargetId((previousNotes) =>
        Object.entries(previousNotes).reduce(
          (nextNotes, [targetId, notes]) => {
            const remainingNotes = notes.filter(
              (currentNote) => currentNote.id !== note.id
            );

            if (remainingNotes.length > 0) {
              nextNotes[targetId] = remainingNotes;
            }

            return nextNotes;
          },
          {} as Record<string, CandidateNote[]>
        )
      );
      setOpenActionsNoteId(null);
      setOpenActionsAnchorRect(null);
      setAlert('Note deleted.', 'success', 3);
    } catch (noteError) {
      setAlert(
        getErrorMessage(noteError, 'Unable to delete note.'),
        'error',
        4
      );
    } finally {
      setDeletingNoteId(null);
    }
  };

  const renderNoteToggleButton = (
    cardKey: string,
    label: string,
    hasNotes = false
  ) => {
    const isOpen = openNoteCardKey === cardKey;

    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={isOpen}
        className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-white/90 text-slate-700 shadow-sm transition hover:bg-white hover:text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
          isOpen ? 'border-sky-400 ring-2 ring-sky-100' : 'border-slate-200'
        }`}
        style={
          hasNotes
            ? {
                color: primaryButtonGreen,
                outline: `2px solid ${primaryButtonGreen}`,
                outlineOffset: '1px'
              }
            : undefined
        }
        onClick={(event) => {
          event.stopPropagation();
          toggleNotePanel(cardKey);
        }}
      >
        <MessageSquarePlus size={18} />
      </button>
    );
  };

  const renderIncludeInSummaryCheckbox = (cardKey: string) => (
    <label
      className="flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-white"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={!!summaryInclusionByCardKey[cardKey]}
        className="h-4 w-4 cursor-pointer accent-[#56dac5]"
        onChange={(event) =>
          setIncludeInSummary(cardKey, event.currentTarget.checked)
        }
      />
      <span>Include in summary</span>
    </label>
  );

  const renderIgnoreSignalButton = (cardKey: string) => {
    const isIgnored = !!ignoredSignalsByCardKey[cardKey];

    return (
      <button
        type="button"
        aria-pressed={isIgnored}
        className={`rounded-full border px-3 py-2 text-xs font-semibold shadow-sm transition ${
          isIgnored
            ? 'border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100'
            : 'border-slate-200 bg-white/90 text-slate-700 hover:bg-white'
        }`}
        onClick={(event) => {
          event.stopPropagation();
          setIgnoredSignal(cardKey, !isIgnored);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {isIgnored ? 'Ignored' : 'Ignore Signal'}
      </button>
    );
  };

  const renderIgnoredSignalMetadata = (
    ignoredSignalMetadata?: IgnoredSignalMetadata | null
  ) => {
    if (!ignoredSignalMetadata) {
      return null;
    }

    const ignoredDate = ignoredSignalMetadata.ignoredAt
      ? new Date(ignoredSignalMetadata.ignoredAt)
      : null;
    const ignoredDateLabel =
      ignoredDate && !Number.isNaN(ignoredDate.getTime())
        ? ignoredDate.toLocaleString()
        : 'Unknown date';

    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-xs text-rose-900">
        <div className="font-semibold uppercase tracking-[0.14em] text-rose-700">
          Ignored Signal
        </div>
        <div className="mt-2 space-y-1">
          <div>
            <span className="font-semibold">Ignored by:</span>{' '}
            {ignoredSignalMetadata.ignoredByName || 'Unknown user'}
          </div>
          <div>
            <span className="font-semibold">Ignored on:</span>{' '}
            {ignoredDateLabel}
          </div>
          {ignoredSignalMetadata.ignoredByUserId ? (
            <div className="break-all">
              <span className="font-semibold">User ID:</span>{' '}
              {ignoredSignalMetadata.ignoredByUserId}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const downloadCompanionDocument = async () => {
    if (companionDocumentSections.length === 0) {
      setAlert(
        'Select at least one candidate or out-of-scope item to include in the summary.',
        'warning',
        4
      );
      return;
    }

    setDownloadingCompanionDocument(true);

    try {
      const blob = await buildNotesDocxBlob({
        headerText: `${companionDocumentBaseName} Companion Document`,
        footerText:
          'Confidential and Proprietary. This document contains sensitive contractual analysis intended solely for the authorized recipient. Unauthorized use, disclosure, copying, or distribution is prohibited.',
        sections: companionDocumentSections
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = `${sanitizeCompanionDocumentFileName(
        companionDocumentBaseName
      )} - Companion Document.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);
      setAlert('Companion document downloaded.', 'success', 3);
    } catch (downloadError) {
      setAlert(
        getErrorMessage(
          downloadError,
          'Unable to generate the companion document.'
        ),
        'error',
        4
      );
    } finally {
      setDownloadingCompanionDocument(false);
    }
  };

  const downloadAnalyzedContractResults = () => {
    if (!originalAnalyzedContractJson) {
      setAlert('The analyzed contract JSON is not loaded.', 'error', 4);
      return;
    }

    const downloadableAnalyzedContractJson = cloneJsonValue(
      originalAnalyzedContractJson
    );

    if (isRecord(downloadableAnalyzedContractJson)) {
      if ('inScopeSignals' in downloadableAnalyzedContractJson) {
        downloadableAnalyzedContractJson.inScopeSignals =
          removeEmbeddingFieldsFromCandidates(
            downloadableAnalyzedContractJson.inScopeSignals
          );
      }

      if ('candidates' in downloadableAnalyzedContractJson) {
        downloadableAnalyzedContractJson.candidates =
          removeEmbeddingFieldsFromCandidates(
            downloadableAnalyzedContractJson.candidates
          );
      }
    }

    const blob = new Blob(
      [JSON.stringify(downloadableAnalyzedContractJson, null, 2)],
      {
        type: 'application/json'
      }
    );
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = `${sanitizeCompanionDocumentFileName(
      companionDocumentBaseName
    )} - Results.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);
  };

  useEffect(() => {
    setNotesByTargetId({});
    setNoteDrafts({});
    setOpenNoteCardKey(null);
    setPostingNoteKey(null);
    setOpenActionsNoteId(null);
    setOpenActionsAnchorRect(null);
    setEditingNoteId(null);
    setEditNoteValue('');
    setUpdatingNoteId(null);
    setDeletingNoteId(null);
    setAnalyzedContractJson(null);
    setOriginalAnalyzedContractJson(null);
    setContextPageNumberById(new Map());
    setBenchmarkRuleIdBySignalId({});
    setSummaryInclusionByCardKey({});
    setPersistedSummaryInclusionByCardKey({});
    setIgnoredSignalsByCardKey({});
    setPersistedIgnoredSignalsByCardKey({});
    setActiveOutOfScopeImpact(null);
    setSavingIncludedItems(false);
    setDownloadingCompanionDocument(false);
    setFallbackPageItems({});
    setShowAllItemsActive(false);
    setActiveCardKey(null);
    setActiveHighlightAllSelectionCardKey(null);
    lastProcessedAnnotationSelectionKeyRef.current = null;
    lastDisplayCardsKeyRef.current = null;
    highlightAllPreparationGenerationRef.current += 1;
    highlightAllPagePreparationPromisesRef.current.clear();
    highlightAllAppliedPageSignaturesRef.current = {};
    highlightAllPreparedPageSignaturesRef.current = {};
    highlightAllPageChangeVersionRef.current += 1;
    void viewerRef.current?.clearAnnotations?.('programmatic');
  }, [documentId, setFallbackPageItems, viewerRef]);

  useEffect(() => {
    if (!openNoteCardKey) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      scrollElementIntoContentView(
        notePanelRefs.current[openNoteCardKey] ?? null,
        {
          behavior: 'smooth',
          block: 'nearest',
          paddingBottom: 32,
          paddingTop: 32
        }
      );
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [openNoteCardKey, scrollElementIntoContentView]);

  useEffect(() => {
    let cancelled = false;

    const loadAnalyzedContract = async () => {
      if (!token || !documentId) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getAnalyzedContract`,
          { documentId },
          auth
        );
        const downloadUrl = presignedUrl?.data?.url;

        if (!downloadUrl || downloadUrl === 'locked') {
          throw new Error('No analyzed contract presigned URL was returned.');
        }

        const response = await axios.get(downloadUrl, {
          responseType: 'json'
        });

        if (cancelled) {
          return;
        }

        try {
          const contextPageMapResponse = await axios.post(
            `${process.env.REACT_APP_KORU_API}/document-contexts?type=contextPageMap`,
            { documentId },
            auth
          );

          if (!cancelled) {
            setContextPageNumberById(
              toContextPageNumberMap(contextPageMapResponse.data.result)
            );
          }
        } catch (_error) {
          if (!cancelled) {
            setContextPageNumberById(new Map());
          }
        }

        setFallbackPageItems(
          groupPdfFallbackTextItemsByPage(response.data?.pageItems)
        );
        const candidateCollection = getCandidateCollection(response.data);
        const outOfScopeCollection = getOutOfScopeCollection(response.data);
        const signalLookup = buildAnalyzedContractSignalLookup(response.data);
        const loadedBenchmarkRuleIdBySignalId = buildBenchmarkRuleIdBySignalId(
          isRecord(response.data) ? response.data.signalComparisons : null
        );
        const loadedEvaluations = extractCandidateEvaluations(
          candidateCollection.entries,
          candidateCollection.pathPrefix,
          signalLookup
        ).map((candidateEvaluation, index) => ({
          ...candidateEvaluation,
          cardKey: `${candidateEvaluation.candidateId}-${index}`
        }));
        const loadedOutOfScopeItems = extractOutOfScopeItems(
          outOfScopeCollection.entries,
          outOfScopeCollection.pathPrefix
        );
        const loadedSummaryInclusionState = buildSummaryInclusionState({
          evaluations: loadedEvaluations,
          outOfScopeItems: loadedOutOfScopeItems
        });
        const loadedIgnoredSignalsState = buildIgnoredSignalsState({
          evaluations: loadedEvaluations,
          outOfScopeItems: loadedOutOfScopeItems
        });

        setAnalyzedContractJson(response.data);
        setOriginalAnalyzedContractJson(cloneJsonValue(response.data));
        setBenchmarkRuleIdBySignalId(loadedBenchmarkRuleIdBySignalId);
        setEvaluations(loadedEvaluations);
        setOutOfScopeItems(loadedOutOfScopeItems);
        setSummaryInclusionByCardKey(loadedSummaryInclusionState);
        setPersistedSummaryInclusionByCardKey(loadedSummaryInclusionState);
        setIgnoredSignalsByCardKey(loadedIgnoredSignalsState);
        setPersistedIgnoredSignalsByCardKey(loadedIgnoredSignalsState);
        setActiveFilter(null);
        setActiveDimensionGroupKey(null);
        setActiveCardKey(null);
        setActiveHighlightAllSelectionCardKey(null);

        try {
          const loadedNotesByTargetId = await getCandidateNotesForDocument({
            documentId,
            auth,
            fallbackOwner: noteOwner
          });

          if (!cancelled) {
            setNotesByTargetId(loadedNotesByTargetId);
          }
        } catch (notesError) {
          if (!cancelled) {
            setNotesByTargetId({});
            setAlert(
              getErrorMessage(notesError, 'Unable to load notes.'),
              'warning',
              4
            );
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          if (axios.isAxiosError(loadError)) {
            setError(loadError.response?.data?.message || loadError.message);
          } else if (loadError instanceof Error) {
            setError(loadError.message);
          } else {
            setError('Unable to load analyzed contract.');
          }
          setEvaluations([]);
          setOutOfScopeItems([]);
          setNotesByTargetId({});
          setAnalyzedContractJson(null);
          setOriginalAnalyzedContractJson(null);
          setContextPageNumberById(new Map());
          setBenchmarkRuleIdBySignalId({});
          setFallbackPageItems({});
          setSummaryInclusionByCardKey({});
          setPersistedSummaryInclusionByCardKey({});
          setIgnoredSignalsByCardKey({});
          setPersistedIgnoredSignalsByCardKey({});
          setActiveCardKey(null);
          setActiveHighlightAllSelectionCardKey(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAnalyzedContract();

    return () => {
      cancelled = true;
    };
  }, [auth, documentId, noteOwner, setAlert, setFallbackPageItems, token]);

  function getPolicyname(policyId: string) {
    return policies.filter((policy) => policy._id === policyId)[0].policyName;
  }

  return (
    <div
      className="relative h-[90vh] min-w-0"
      style={{ scrollbarWidth: 'none', scrollbarColor: 'red' }}
      // style={{ scrollbarWidth: 'thin', scrollbarColor: '#888888 transparent' }}
    >
      <div className="mx-auto flex h-[90vh] min-h-0 flex-col px-2 py-2">
        <Container
          fitHeight
          className="overflow-hidden rounded-2xl border-slate-200 shadow-[0_18px_48px_rgba(15,23,42,0.08)]"
          header={
            <Header
              variant="h1"
              description={
                <>
                  <p>
                    {currentDoc?.documentFileName ||
                      documentId ||
                      'Analyzed contract viewer'}
                  </p>
                  {policies.length > 0 && currentDoc?.policyId && (
                    <div className=" ">
                      <p className="text-xl font-bold">Benchmark Document</p>
                      <p>
                        {currentDoc?.policyId
                          ? getPolicyname(currentDoc?.policyId)
                          : ''}
                      </p>
                    </div>
                  )}
                </>
              }
              actions={
                <div className="flex max-w-full flex-wrap justify-end gap-2">
                  <Button
                    onClick={() => void toggleShowAllItems()}
                    disabled={highlightAllAnnotationRequests.length === 0}
                    variant={showAllItemsActive ? 'primary' : 'normal'}
                  >
                    {showAllItemsActive
                      ? 'Highlight all ON'
                      : 'Highlight all OFF'}
                  </Button>
                  <div className="relative" ref={headerActionsMenuRef}>
                    <button
                      type="button"
                      aria-label="Show more actions"
                      aria-expanded={isHeaderActionsMenuOpen}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#56dac5]/40"
                      onClick={() =>
                        setIsHeaderActionsMenuOpen(
                          (currentValue) => !currentValue
                        )
                      }
                    >
                      <MoreVertical size={18} />
                    </button>
                    {isHeaderActionsMenuOpen ? (
                      <div className="absolute right-0 top-full z-30 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl ring-1 ring-black/5">
                        <button
                          type="button"
                          disabled={
                            !hasUnsavedAnalysisChanges || savingIncludedItems
                          }
                          className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                          onClick={() => {
                            setIsHeaderActionsMenuOpen(false);
                            void saveAnalyzedContractChanges();
                          }}
                        >
                          {savingIncludedItems ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          type="button"
                          disabled={
                            includedSummaryCount === 0 ||
                            downloadingCompanionDocument
                          }
                          className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                          onClick={() => {
                            setIsHeaderActionsMenuOpen(false);
                            void downloadCompanionDocument();
                          }}
                        >
                          {downloadingCompanionDocument
                            ? 'Downloading...'
                            : 'Download Companion Document'}
                        </button>
                        {isDeveloper ? (
                          <button
                            type="button"
                            disabled={!originalAnalyzedContractJson}
                            className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                            onClick={() => {
                              setIsHeaderActionsMenuOpen(false);
                              downloadAnalyzedContractResults();
                            }}
                          >
                            Download Results
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              }
            >
              Analyzed Contract Viewer
            </Header>
          }
        >
          <SpaceBetween
            className="h-full min-h-0 overflow-y-auto bg-slate-50/40"
            ref={scrollContentRef}
            size="l"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: `${primaryButtonGreen} transparent`
            }}
          >
            {!documentId ? (
              <Alert type="error">
                Missing `documentId` in the search params.
              </Alert>
            ) : null}

            {error ? <Alert type="error">{error}</Alert> : null}

            {loading ? (
              <Box textAlign="center" padding={{ vertical: 'xl' }}>
                <Spinner size="large" />
              </Box>
            ) : null}

            {!loading && !error && !hasAnalysisContent ? (
              <Box margin={{ vertical: 'xs' }} textAlign="center">
                No analyzed contract items found.
              </Box>
            ) : null}

            {!loading && hasAnalysisContent ? (
              <SpaceBetween size="l">
                <Container
                  header={
                    <Header
                      variant="h2"
                      description={
                        activeFilter
                          ? `Filtering to ${formatAnalysisFilterLabel(
                              activeFilter
                            )}`
                          : 'Click a result card to filter the contract analysis.'
                      }
                    >
                      Comparison Results
                    </Header>
                  }
                >
                  <SpaceBetween size="m">
                    <Box color="text-body-secondary">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          {includedSummaryCount} item
                          {includedSummaryCount === 1 ? '' : 's'} included in
                          the summary. {ignoredSignalsCount} ignored signal
                          {ignoredSignalsCount === 1 ? '' : 's'}.
                          {hasUnsavedAnalysisChanges
                            ? ' There are unsaved changes.'
                            : ''}
                        </span>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            aria-expanded={isSearchInputOpen}
                            aria-label={
                              isSearchInputOpen
                                ? 'Close contract search'
                                : 'Open contract search'
                            }
                            className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition ${
                              isSearchInputOpen || hasActiveSearchQuery
                                ? 'border-sky-300 bg-sky-50 text-sky-700'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                            onClick={toggleSearchInput}
                          >
                            <Search className="h-4 w-4" />
                          </button>
                          <div
                            className={`overflow-hidden transition-all duration-200 ${
                              isSearchInputOpen
                                ? 'w-full opacity-100 sm:w-80'
                                : 'w-0 opacity-0'
                            }`}
                          >
                            <div className="relative">
                              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                placeholder="Search signal ID or clause"
                                className="h-10 w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                                onChange={(event) =>
                                  setSearchQuery(event.target.value)
                                }
                              />
                            </div>
                          </div>
                          {isSearchInputOpen && searchQuery ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                              onClick={() => setSearchQuery('')}
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </Box>

                    <div className="space-y-2">
                      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-2">
                        <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Comparison
                        </div>
                        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
                          {COMPARISON_RESULT_OPTIONS.map((result) => {
                            const isActive = activeFilter === result;

                            return (
                              <button
                                key={result}
                                type="button"
                                className={`rounded-lg border px-2.5 py-2 text-left transition duration-150 ${getAnalysisFilterCardClasses(
                                  result,
                                  isActive
                                )}`}
                                onClick={() =>
                                  handleAnalysisFilterChange(result)
                                }
                              >
                                <div className="text-xs font-semibold leading-tight">
                                  {renderComparisonResultFilterLabel(result)}
                                </div>
                                <div className="mt-2 flex items-end justify-between gap-2">
                                  <div className="text-xl font-semibold leading-none sm:text-2xl">
                                    {comparisonResultCounts[result]}
                                  </div>
                                  <div className="text-[9px] font-medium uppercase tracking-[0.12em] text-current/80 sm:text-[10px]">
                                    {isActive ? 'On' : 'View'}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-2">
                        <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Workflow
                        </div>
                        <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                          {secondaryAnalysisFilterOptions.map((result) => {
                            const isActive = activeFilter === result;

                            return (
                              <button
                                key={result}
                                type="button"
                                className={`rounded-lg border px-2.5 py-2 text-left transition duration-150 ${getAnalysisFilterCardClasses(
                                  result,
                                  isActive
                                )}`}
                                onClick={() =>
                                  handleAnalysisFilterChange(result)
                                }
                              >
                                <div className="text-xs font-semibold leading-tight">
                                  {formatAnalysisFilterLabel(result)}
                                </div>
                                <div className="mt-2 flex items-end justify-between gap-2">
                                  <div className="text-xl font-semibold leading-none sm:text-2xl">
                                    {comparisonResultCounts[result]}
                                  </div>
                                  <div className="text-[9px] font-medium uppercase tracking-[0.12em] text-current/80 sm:text-[10px]">
                                    {isActive ? 'On' : 'View'}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-2">
                        <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Dimension
                        </div>
                        <Select
                          className="w-full"
                          disabled={dimensionFilterOptions.length === 0}
                          options={dimensionFilterOptions}
                          placeholder={
                            dimensionFilterOptions.length > 0
                              ? 'All present dimensions'
                              : 'No dimensions available'
                          }
                          selectedOption={selectedDimensionFilterOption}
                          onChange={({ detail }) =>
                            setActiveDimensionGroupKey(
                              detail.selectedOption?.value ?? null
                            )
                          }
                        />
                      </div>
                    </div>

                    {activeFilter ||
                    activeDimensionGroupKey ||
                    activeOutOfScopeImpact ||
                    hasActiveSearchQuery ? (
                      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="text-sm text-slate-700">
                          Showing {visibleItemCount}{' '}
                          {hasActiveSearchQuery
                            ? `matching card${visibleItemCount === 1 ? '' : 's'}`
                            : showingNotesItems
                              ? `noted item${visibleItemCount === 1 ? '' : 's'}`
                              : showingIncludedSummaryItems
                                ? `included item${visibleItemCount === 1 ? '' : 's'}`
                                : showingIgnoredItems
                                  ? `ignored item${visibleItemCount === 1 ? '' : 's'}`
                                  : showingOutOfScopeItems
                                    ? `out-of-scope item${visibleItemCount === 1 ? '' : 's'}`
                                    : `candidate${visibleItemCount === 1 ? '' : 's'}`}
                          {!hasActiveSearchQuery && activeFilter ? (
                            <>
                              {' '}
                              with{' '}
                              <strong>
                                {formatAnalysisFilterLabel(activeFilter)}
                              </strong>
                            </>
                          ) : null}
                          {!hasActiveSearchQuery &&
                          activeOutOfScopeImpact &&
                          (showingOutOfScopeItems ||
                            showingNotesItems ||
                            showingIncludedSummaryItems ||
                            showingIgnoredItems) ? (
                            <>
                              {' '}
                              with{' '}
                              <strong>
                                {formatImpactLabel(activeOutOfScopeImpact)}
                              </strong>
                            </>
                          ) : null}
                          {hasActiveSearchQuery ? (
                            <>
                              {' '}
                              across <strong>all cards</strong> matching{' '}
                              <strong>{searchQuery.trim()}</strong>
                            </>
                          ) : null}
                          {!hasActiveSearchQuery &&
                          activeDimensionFilterLabel ? (
                            <>
                              {' '}
                              in <strong>{activeDimensionFilterLabel}</strong>
                            </>
                          ) : null}
                          .
                        </div>
                        <div className="flex items-center gap-2">
                          {activeDimensionGroupKey ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 transition hover:bg-white"
                              onClick={() => setActiveDimensionGroupKey(null)}
                            >
                              Clear Dimension Filter
                            </button>
                          ) : null}
                          {activeFilter ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 transition hover:bg-white"
                              onClick={() =>
                                activeFilter
                                  ? handleAnalysisFilterChange(activeFilter)
                                  : undefined
                              }
                            >
                              Clear Filter
                            </button>
                          ) : null}
                          {activeOutOfScopeImpact ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 transition hover:bg-white"
                              onClick={() => setActiveOutOfScopeImpact(null)}
                            >
                              Clear Impact Filter
                            </button>
                          ) : null}
                          {hasActiveSearchQuery ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 transition hover:bg-white"
                              onClick={() => {
                                setSearchQuery('');
                                setIsSearchInputOpen(false);
                              }}
                            >
                              Clear Search
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </SpaceBetween>
                </Container>

                {/* <Header counter={`(${visibleItemCount})`}>
                  Analysis Items
                </Header> */}

                <div key={displayCardsKey} className="space-y-5">
                  {visibleItemCount === 0 ? (
                    <Box margin={{ vertical: 'xs' }} textAlign="center">
                      {hasActiveSearchQuery
                        ? 'No items match the current search.'
                        : showingNotesItems
                          ? 'No noted items found.'
                          : showingIncludedSummaryItems
                            ? 'No items are currently included in summary.'
                            : showingOutOfScopeItems
                              ? 'No out-of-scope items found.'
                              : 'No candidates match the selected filters.'}
                    </Box>
                  ) : null}

                  {visibleOutOfScopeEntries.map(({ item, index }) => {
                    const outOfScopeContextIds = getOutOfScopeContextIds(item);
                    const outOfScopePageNumbers = getResolvedPageNumbers({
                      contextIds: outOfScopeContextIds,
                      contextPageMap
                    });
                    const outOfScopeBenchmarkRuleId =
                      benchmarkRuleIdBySignalId[
                        normalizeNoteTargetId(item.signal_id)
                      ];
                    const outOfScopeContextLabel =
                      getPrimaryContextId(outOfScopeContextIds);
                    const canHighlightOutOfScope = Boolean(
                      getOutOfScopeSourceText(item).trim()
                    );
                    const outOfScopeKey = getOutOfScopeCardKey(item, index);
                    const outOfScopeId = getOutOfScopeTargetId(
                      item,
                      outOfScopeKey
                    );
                    const isActive = showAllItemsActive
                      ? activeHighlightAllSelectionCardKey === outOfScopeKey
                      : activeCardKey === outOfScopeKey;
                    const outOfScopeNotes = getNotesForTarget({
                      notesByTargetId,
                      targetId: outOfScopeId,
                      fallbackTargetIds: getOutOfScopeFallbackTargetIds(
                        item,
                        index
                      )
                    });
                    const toggleOutOfScopeHighlight = async () => {
                      const query = getOutOfScopeSourceText(item).trim();

                      if (!query) {
                        setAlert(
                          'No source text was found for this out-of-scope item.',
                          'error',
                          4
                        );
                        return;
                      }

                      if (outOfScopePageNumbers.length === 0) {
                        setAlert(
                          formatMissingResolvedPageMessage(
                            outOfScopeContextIds
                          ),
                          'error',
                          4
                        );
                        return;
                      }

                      await highlightSingleItem(
                        outOfScopeKey,
                        {
                          pageNumber: outOfScopePageNumbers[0],
                          pageNumbers: outOfScopePageNumbers,
                          query,
                          reference: buildOutOfScopeAnnotationReference(
                            item,
                            index,
                            outOfScopePageNumbers
                          )
                        },
                        isActive
                      );
                    };

                    return (
                      <div
                        key={outOfScopeKey}
                        ref={(node) => {
                          cardRefs.current[outOfScopeKey] = node;
                        }}
                        role="button"
                        tabIndex={0}
                        aria-pressed={isActive}
                        className={`block w-full cursor-pointer rounded-2xl text-left transition focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                          isActive ? 'ring-1 ring-inset ring-sky-400' : ''
                        }`}
                        onClick={() => void toggleOutOfScopeHighlight()}
                        onKeyDown={(event) =>
                          handleInteractiveCardKeyDown(
                            event,
                            () => void toggleOutOfScopeHighlight()
                          )
                        }
                      >
                        <Container
                          className={`transition-colors transition-shadow ${
                            isActive
                              ? 'border-sky-300 bg-sky-50/40 shadow-[0_0_0_1px_rgba(14,165,233,0.14),0_14px_30px_rgba(15,23,42,0.06)]'
                              : 'hover:border-slate-300 hover:bg-slate-50/60 hover:shadow-sm'
                          }`}
                          header={
                            <Header
                              variant="h3"
                              description={
                                canHighlightOutOfScope
                                  ? isActive
                                    ? 'Highlighted'
                                    : 'Click to highlight'
                                  : 'No evidence available'
                              }
                              actions={
                                <div className="flex max-w-full min-w-0 flex-col items-end gap-1.5">
                                  <SpaceBetween
                                    direction="horizontal"
                                    size="xs"
                                    className="justify-end"
                                  >
                                    {item.impact ? (
                                      <Popover
                                        dismissButton={false}
                                        position="top"
                                        size="small"
                                        triggerType="custom"
                                        content="Click to filter by Impact type."
                                      >
                                        <button
                                          type="button"
                                          aria-pressed={
                                            activeOutOfScopeImpact ===
                                            item.impact
                                          }
                                          className={`rounded-full border bg-white/80 px-3 py-2 text-xs font-semibold text-current shadow-sm transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-current/25 ${
                                            activeOutOfScopeImpact ===
                                            item.impact
                                              ? 'border-current/40 ring-2 ring-current/15'
                                              : 'border-current/15'
                                          }`}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setActiveOutOfScopeImpact(
                                              (currentValue) =>
                                                currentValue === item.impact
                                                  ? null
                                                  : (item.impact ?? null)
                                            );
                                          }}
                                          onKeyDown={(event) =>
                                            event.stopPropagation()
                                          }
                                        >
                                          {formatImpactLabel(item.impact)}
                                        </button>
                                      </Popover>
                                    ) : null}
                                    {renderIncludeInSummaryCheckbox(
                                      outOfScopeKey
                                    )}
                                    {renderIgnoreSignalButton(outOfScopeKey)}
                                    {renderNoteToggleButton(
                                      outOfScopeKey,
                                      `Add note for ${renderValue(
                                        item.signal_id || outOfScopeContextLabel
                                      )}`,
                                      outOfScopeNotes.length > 0
                                    )}
                                  </SpaceBetween>
                                  {item.signal_id ? (
                                    <div className="max-w-full break-all text-right text-[11px] font-medium text-slate-500">
                                      Signal ID:{' '}
                                      <span className="text-slate-700">
                                        {item.signal_id}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              }
                            >
                              {renderComparisonResultHeaderContent(
                                'not_applicable'
                              )}
                            </Header>
                          }
                        >
                          <SpaceBetween size="l">
                            {renderReadOnlyField(
                              'Clause being reviewed',
                              renderValue(getOutOfScopeSourceText(item))
                            )}

                            {renderReadOnlyField(
                              'Why this is out of scope',
                              'No benchmark policy matches this clause.'
                            )}

                            {renderReadOnlyField(
                              'Things to keep an eye on',
                              renderValue(item.reason),
                              'border-amber-200 bg-amber-50'
                            )}

                            {renderCardSourceMetadata({
                              contextIds: outOfScopeContextIds,
                              pageNumbers: outOfScopePageNumbers,
                              benchmarkRuleId: outOfScopeBenchmarkRuleId,
                              showContextIds: isDeveloper
                            })}

                            {renderIgnoredSignalMetadata(
                              ignoredSignalsByCardKey[outOfScopeKey]
                            )}
                          </SpaceBetween>
                        </Container>

                        {openNoteCardKey === outOfScopeKey ||
                        outOfScopeNotes.length > 0 ? (
                          <div className="px-4 pb-4 pt-3">
                            <CandidateNotesPanel
                              notes={outOfScopeNotes}
                              noteValue={noteDrafts[outOfScopeKey] || ''}
                              isPosting={postingNoteKey === outOfScopeKey}
                              showComposer={openNoteCardKey === outOfScopeKey}
                              currentUserId={currentUserId}
                              openActionsNoteId={openActionsNoteId}
                              openActionsAnchorRect={openActionsAnchorRect}
                              editingNoteId={editingNoteId}
                              editNoteValue={editNoteValue}
                              updatingNoteId={updatingNoteId}
                              deletingNoteId={deletingNoteId}
                              notePanelRef={(node) => {
                                notePanelRefs.current[outOfScopeKey] = node;
                              }}
                              onNoteChange={(value) =>
                                setNoteDraft(outOfScopeKey, value)
                              }
                              onPost={() =>
                                postCandidateNote({
                                  cardKey: outOfScopeKey,
                                  target: {
                                    type: 'out_of_scope',
                                    outOfScopeId,
                                    contextIds: outOfScopeContextIds
                                  }
                                })
                              }
                              onToggleActions={(noteId, anchorRect) => {
                                if (openActionsNoteId === noteId) {
                                  setOpenActionsNoteId(null);
                                  setOpenActionsAnchorRect(null);
                                  return;
                                }

                                setOpenActionsNoteId(noteId);
                                setOpenActionsAnchorRect(anchorRect);
                              }}
                              onCloseActions={() => {
                                setOpenActionsNoteId(null);
                                setOpenActionsAnchorRect(null);
                              }}
                              onStartEdit={startEditingNote}
                              onEditNoteChange={setEditNoteValue}
                              onCancelEdit={cancelEditingNote}
                              onSaveEdit={saveEditedNote}
                              onDelete={deleteNote}
                            />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}

                  {shouldRenderCandidateGroups
                    ? visibleGroupedEvaluations.map(
                        ({ benchmarkDimension, evaluatedDimension, items }) => {
                          const groupKey = `${benchmarkDimension}|||${evaluatedDimension}`;
                          const isGroupActive =
                            activeDimensionGroupKey === groupKey;

                          return (
                            <div
                              key={`${benchmarkDimension}-${evaluatedDimension}`}
                              className="space-y-4"
                            >
                              <button
                                type="button"
                                aria-pressed={isGroupActive}
                                className={`block w-full rounded-2xl border px-5 py-4 text-left transition duration-150 ${
                                  isGroupActive
                                    ? 'border-slate-300 bg-slate-100 text-slate-900 ring-1 ring-inset ring-slate-300 shadow-sm'
                                    : 'border-slate-200 bg-slate-50 text-slate-900 opacity-90 hover:opacity-100 hover:-translate-y-0.5'
                                }`}
                                onClick={() =>
                                  setActiveDimensionGroupKey((currentValue) =>
                                    currentValue === groupKey ? null : groupKey
                                  )
                                }
                              >
                                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                  Dimension
                                </div>
                                <div className="mt-1 text-sm font-semibold leading-snug text-slate-900">
                                  {formatDimensionGroupDisplayLabel(
                                    benchmarkDimension,
                                    evaluatedDimension
                                  )}
                                </div>
                              </button>

                              <div className="space-y-4">
                                {items.map(
                                  ({
                                    candidateId,
                                    contextIds,
                                    evaluation,
                                    index,
                                    cardKey: storedCardKey,
                                    signalId
                                  }) => {
                                    const candidatePageNumbers =
                                      getResolvedPageNumbers({
                                        contextIds,
                                        contextPageMap
                                      });
                                    const comparisonResult =
                                      isComparisonResultFilter(
                                        evaluation.comparison_result
                                      )
                                        ? evaluation.comparison_result
                                        : null;
                                    const confidence = evaluation.confidence
                                      ? evaluation.confidence
                                      : null;
                                    const cardKey =
                                      storedCardKey ||
                                      `${candidateId}-${index}`;
                                    const benchmarkRuleId =
                                      benchmarkRuleIdBySignalId[
                                        normalizeNoteTargetId(signalId)
                                      ];
                                    const canHighlightCandidate = Boolean(
                                      evaluation.evidence?.evaluated_source_text.trim()
                                    );
                                    const isActive = showAllItemsActive
                                      ? activeHighlightAllSelectionCardKey ===
                                        cardKey
                                      : activeCardKey === cardKey;
                                    const notes = getNotesForTarget({
                                      notesByTargetId,
                                      targetId: candidateId
                                    });
                                    const toggleCandidateHighlight =
                                      async () => {
                                        const query =
                                          evaluation.evidence?.evaluated_source_text.trim();

                                        if (!query) {
                                          setAlert(
                                            'No source text was found for this candidate.',
                                            'error',
                                            4
                                          );
                                          return;
                                        }

                                        if (candidatePageNumbers.length === 0) {
                                          setAlert(
                                            formatMissingResolvedPageMessage(
                                              contextIds
                                            ),
                                            'error',
                                            4
                                          );
                                          return;
                                        }
                                        console.log(
                                          'in single click page numbers',
                                          candidatePageNumbers
                                        );
                                        await highlightSingleItem(
                                          cardKey,
                                          {
                                            pageNumber: candidatePageNumbers[0],
                                            pageNumbers: candidatePageNumbers,
                                            query,
                                            reference:
                                              buildCandidateAnnotationReference(
                                                {
                                                  candidateId,
                                                  contextIds,
                                                  cardKey,
                                                  evaluation
                                                },
                                                index,
                                                candidatePageNumbers
                                              )
                                          },
                                          isActive
                                        );
                                      };

                                    return (
                                      <div
                                        key={cardKey}
                                        ref={(node) => {
                                          cardRefs.current[cardKey] = node;
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-pressed={isActive}
                                        className={`block w-full cursor-pointer rounded-2xl text-left transition focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                                          isActive
                                            ? 'ring-1 ring-inset ring-sky-400'
                                            : ''
                                        }`}
                                        onClick={() =>
                                          void toggleCandidateHighlight()
                                        }
                                        onKeyDown={(event) =>
                                          handleInteractiveCardKeyDown(
                                            event,
                                            () =>
                                              void toggleCandidateHighlight()
                                          )
                                        }
                                      >
                                        <Container
                                          className={`transition-colors transition-shadow ${
                                            isActive
                                              ? 'border-sky-300 bg-sky-50/40 shadow-[0_0_0_1px_rgba(14,165,233,0.14),0_14px_30px_rgba(15,23,42,0.06)]'
                                              : 'hover:border-slate-300 hover:bg-slate-50/60 hover:shadow-sm'
                                          }`}
                                          header={
                                            <Header
                                              variant="h3"
                                              description={
                                                canHighlightCandidate
                                                  ? isActive
                                                    ? 'Highlighted'
                                                    : 'Click to highlight'
                                                  : 'No evidence available'
                                              }
                                              actions={
                                                <div className="flex max-w-full min-w-0 flex-col items-end gap-1.5">
                                                  <SpaceBetween
                                                    direction="horizontal"
                                                    size="xs"
                                                    className="justify-end"
                                                  >
                                                    {renderIncludeInSummaryCheckbox(
                                                      cardKey
                                                    )}
                                                    {renderIgnoreSignalButton(
                                                      cardKey
                                                    )}
                                                    {renderNoteToggleButton(
                                                      cardKey,
                                                      `Add note for ${candidateId}`,
                                                      notes.length > 0
                                                    )}
                                                  </SpaceBetween>
                                                  {signalId ? (
                                                    <div className="max-w-full break-all text-right text-[11px] font-medium text-slate-500">
                                                      Signal ID:{' '}
                                                      <span className="text-slate-700">
                                                        {signalId}
                                                      </span>
                                                    </div>
                                                  ) : null}
                                                </div>
                                              }
                                            >
                                              {renderComparisonResultHeaderContent(
                                                comparisonResult,
                                                confidence,
                                                candidateId
                                              )}
                                            </Header>
                                          }
                                        >
                                          <SpaceBetween size="l">
                                            {renderReadOnlyField(
                                              'Clause being reviewed',
                                              renderValue(
                                                evaluation.evidence
                                                  ?.evaluated_source_text
                                              )
                                            )}

                                            {renderReadOnlyField(
                                              'Your Standard',
                                              renderValue(
                                                evaluation.basis
                                                  ?.benchmark_summary
                                              )
                                            )}

                                            {renderReadOnlyField(
                                              `Why this is ${COMPARISON_NORMALIZED_TEXT[evaluation?.comparison_result!]?.text ?? evaluation?.comparison_result ?? ''}`,
                                              renderValue(
                                                evaluation.basis?.reason
                                              )
                                            )}

                                            {evaluation?.watchpoints &&
                                            evaluation?.watchpoints?.length > 0
                                              ? renderReadOnlyField(
                                                  'Things to keep an eye on',
                                                  renderWatchPoints(
                                                    evaluation.watchpoints
                                                  ),
                                                  'border-amber-200 bg-amber-50'
                                                )
                                              : null}

                                            {renderCardSourceMetadata({
                                              contextIds,
                                              pageNumbers: candidatePageNumbers,
                                              benchmarkRuleId,
                                              showContextIds: isDeveloper
                                            })}

                                            {renderIgnoredSignalMetadata(
                                              ignoredSignalsByCardKey[cardKey]
                                            )}
                                          </SpaceBetween>
                                        </Container>

                                        {openNoteCardKey === cardKey ||
                                        notes.length > 0 ? (
                                          <div className="px-4 pb-4 pt-3">
                                            <CandidateNotesPanel
                                              notes={notes}
                                              noteValue={
                                                noteDrafts[cardKey] || ''
                                              }
                                              isPosting={
                                                postingNoteKey === cardKey
                                              }
                                              showComposer={
                                                openNoteCardKey === cardKey
                                              }
                                              currentUserId={currentUserId}
                                              openActionsNoteId={
                                                openActionsNoteId
                                              }
                                              openActionsAnchorRect={
                                                openActionsAnchorRect
                                              }
                                              editingNoteId={editingNoteId}
                                              editNoteValue={editNoteValue}
                                              updatingNoteId={updatingNoteId}
                                              deletingNoteId={deletingNoteId}
                                              notePanelRef={(node) => {
                                                notePanelRefs.current[cardKey] =
                                                  node;
                                              }}
                                              onNoteChange={(value) =>
                                                setNoteDraft(cardKey, value)
                                              }
                                              onPost={() =>
                                                postCandidateNote({
                                                  cardKey,
                                                  target: {
                                                    type: 'candidate',
                                                    candidateId,
                                                    contextIds
                                                  }
                                                })
                                              }
                                              onToggleActions={(
                                                noteId,
                                                anchorRect
                                              ) => {
                                                if (
                                                  openActionsNoteId === noteId
                                                ) {
                                                  setOpenActionsNoteId(null);
                                                  setOpenActionsAnchorRect(
                                                    null
                                                  );
                                                  return;
                                                }

                                                setOpenActionsNoteId(noteId);
                                                setOpenActionsAnchorRect(
                                                  anchorRect
                                                );
                                              }}
                                              onCloseActions={() => {
                                                setOpenActionsNoteId(null);
                                                setOpenActionsAnchorRect(null);
                                              }}
                                              onStartEdit={startEditingNote}
                                              onEditNoteChange={
                                                setEditNoteValue
                                              }
                                              onCancelEdit={cancelEditingNote}
                                              onSaveEdit={saveEditedNote}
                                              onDelete={deleteNote}
                                            />
                                          </div>
                                        ) : null}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          );
                        }
                      )
                    : null}
                </div>
              </SpaceBetween>
            ) : null}
          </SpaceBetween>
          <ScrollDownArrow containerRef={scrollContentRef} bottomValue="1rem" />
        </Container>
      </div>
    </div>
  );
};

export default AnalyzedContractViewer;
